import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Switch, Route } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import Loading from '../Global/Loading';
import { Styles } from './Styles/MerchantListStyles';
import MerchantListHeader from './MerchantListHeader';
import StoreEntitlementsPanel from './StoreEntitlementsPanel';
import LandingContainer from '../Inventory/LandingContainer';
import User from '../Profiles/User';
import CreateMerchantWizard from '../../views/forms/CreateMerchantWizard';
import CreateSubMerchantWizard from '../../views/forms/CreateSubMerchantWizard';

import * as inventory_urls from '../../urls/api-inventory/admin';
import * as user_urls from '../../urls/api-users/admin';
import * as merchant_urls from '../../urls/api-users/merchant';
import config from '../../config';
import getRoles from '../../Auth/getRoles';
import { useGlobalContext } from '../../context';
import formatPhone from '../../helpers/phone';
import {
  fetchPaymentProfile as fetchSelfPaymentProfile,
  fetchStorePaymentProfile,
  getStoredStoreInvoiceFrequency,
  getPaymentTermLabel,
  getPaymentTermSelectValue,
  isKnownPaymentTermValue,
  normalizeInvoiceFrequency,
  setStoredStoreInvoiceFrequency
} from '../../services/paymentService';
import { applyStoredStoreDeliveryTypeOverride, setStoredStoreDeliveryTypeOverride } from '../../services/storeDeliveryTypeOverrideService';
import { isDashboardLocalDebugMode, isLocalLikeHost } from '../../utils/environmentMode';
import { buildChangedUserEditPayload, getUserEditResponsePayload, validateUserEditResponse } from '../../utils/userEditResponse';

const HOURS_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DELIVERY_TYPE_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'multidrop', label: 'Multidrop' }
];
const ASSOCIATED_USER_ROLE_OPTIONS = [
  { value: '220', label: 'Store Manager' },
  { value: '230', label: 'Inventory Manager' },
  { value: '240', label: 'Delivery Manager' }
];

const ASSOCIATED_USER_ROLE_LABELS = ASSOCIATED_USER_ROLE_OPTIONS.reduce((labels, option) => {
  labels[option.value] = option.label;
  return labels;
}, {});

const USER_EDIT_REQUEST_TIMEOUT_MS = 15000;
const MERCHANT_LIST_REQUEST_TIMEOUT_MS = 12000;
const CURRENT_USER_STORE_CACHE_KEY = 'merchant_current_store_id_by_auth0';
const MERCHANT_STORE_ACCESS_FAST_TIMEOUT_MS = 1800;
const STORE_PAYMENT_PROFILE_CACHE_KEY = 'merchant_store_payment_profile_by_id';
const STORE_PAYMENT_PROFILE_FAST_TIMEOUT_MS = 3000;

const readCurrentUserStoreCache = () => {
  try {
    const rawValue = localStorage.getItem(CURRENT_USER_STORE_CACHE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue);

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch (error) {
    return {};
  }
};

const writeCurrentUserStoreCache = nextCache => {
  try {
    localStorage.setItem(CURRENT_USER_STORE_CACHE_KEY, JSON.stringify(nextCache || {}));
  } catch (error) {
    // Ignore localStorage write failures.
  }
};

const getStoredCurrentUserStoreId = auth0Id => {
  if (!auth0Id) {
    return '';
  }

  const cache = readCurrentUserStoreCache();
  const cachedValue = cache[String(auth0Id)];

  return typeof cachedValue === 'string' ? cachedValue : '';
};

const setStoredCurrentUserStoreId = (auth0Id, storeId) => {
  if (!auth0Id) {
    return;
  }

  const cache = readCurrentUserStoreCache();
  const nextCache = {
    ...cache,
    [String(auth0Id)]: storeId ? String(storeId) : '',
  };

  writeCurrentUserStoreCache(nextCache);
};

const readStorePaymentProfileCache = () => {
  try {
    const rawValue = localStorage.getItem(STORE_PAYMENT_PROFILE_CACHE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue);

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch (error) {
    return {};
  }
};

const getStoredStorePaymentProfile = storeId => {
  if (!storeId) {
    return null;
  }

  const cache = readStorePaymentProfileCache();
  const cachedValue = cache[String(storeId)];

  if (!cachedValue || typeof cachedValue !== 'object') {
    return null;
  }

  return {
    billing_email: typeof cachedValue.billing_email === 'string' ? cachedValue.billing_email : '',
    invoice_frequency: typeof cachedValue.invoice_frequency === 'string' ? cachedValue.invoice_frequency : ''
  };
};

const setStoredStorePaymentProfile = (storeId, paymentProfile) => {
  if (!storeId || !paymentProfile || typeof paymentProfile !== 'object') {
    return;
  }

  const cache = readStorePaymentProfileCache();
  const nextCache = {
    ...cache,
    [String(storeId)]: {
      billing_email: typeof paymentProfile.billing_email === 'string' ? paymentProfile.billing_email : '',
      invoice_frequency: typeof paymentProfile.invoice_frequency === 'string' ? paymentProfile.invoice_frequency : ''
    }
  };

  try {
    localStorage.setItem(STORE_PAYMENT_PROFILE_CACHE_KEY, JSON.stringify(nextCache));
  } catch (error) {
    // Ignore localStorage write failures.
  }
};

const createEmptyHours = () =>
  HOURS_DAYS.map((label, index) => ({
    dayLabel: label,
    day_of_week: index,
    open_time: '',
    close_time: '',
    is_closed: true
  }));

const normalizeStoreTimes = storeTimes => {
  const defaultHours = createEmptyHours();

  (storeTimes || []).forEach(day => {
    const dayIndex = Number(day.day_of_week);

    if (Number.isNaN(dayIndex) || !defaultHours[dayIndex]) {
      return;
    }

    defaultHours[dayIndex] = {
      dayLabel: HOURS_DAYS[dayIndex],
      day_of_week: dayIndex,
      open_time: day.open_time || '',
      close_time: day.close_time || '',
      is_closed: day.is_closed || (!day.open_time && !day.close_time)
    };
  });

  return defaultHours;
};

const sortUsersByName = users =>
  [...users].sort((leftUser, rightUser) => {
    const leftName = `${leftUser.firstname || ''} ${leftUser.lastname || ''}`.trim().toLowerCase();
    const rightName = `${rightUser.firstname || ''} ${rightUser.lastname || ''}`.trim().toLowerCase();

    return leftName.localeCompare(rightName);
  });

const getAssociatedUserDedupeKey = user =>
  (user && user.auth0_id && String(user.auth0_id))
  || (user && user.email && String(user.email).toLowerCase())
  || (user && user.user_id && `user:${String(user.user_id)}`)
  || '';

const scoreAssociatedUserCompleteness = user => {
  if (!user || typeof user !== 'object') {
    return 0;
  }

  let score = 0;

  if (user.firstname) {
    score += 2;
  }

  if (user.lastname) {
    score += 2;
  }

  if (user.email) {
    score += 3;
  }

  if (user.phone) {
    score += 2;
  }

  if (user.username) {
    score += 1;
  }

  if (Array.isArray(user.Roles)) {
    score += user.Roles.length;
  }

  if (Array.isArray(user.access_users)) {
    score += Math.min(user.access_users.length, 3);
  }

  return score;
};

const mergeAssociatedUsers = (existingUser, candidateUser) => {
  const existingRoles = Array.isArray(existingUser && existingUser.Roles) ? existingUser.Roles : [];
  const candidateRoles = Array.isArray(candidateUser && candidateUser.Roles) ? candidateUser.Roles : [];
  const existingAccessUsers = Array.isArray(existingUser && existingUser.access_users) ? existingUser.access_users : [];
  const candidateAccessUsers = Array.isArray(candidateUser && candidateUser.access_users) ? candidateUser.access_users : [];

  const mergedUser = {
    ...(existingUser || {}),
    ...(candidateUser || {}),
    user_id: (candidateUser && candidateUser.user_id) || (existingUser && existingUser.user_id) || '',
    auth0_id: (candidateUser && candidateUser.auth0_id) || (existingUser && existingUser.auth0_id) || '',
    username: (candidateUser && candidateUser.username) || (existingUser && existingUser.username) || '',
    firstname: (candidateUser && candidateUser.firstname) || (existingUser && existingUser.firstname) || '',
    lastname: (candidateUser && candidateUser.lastname) || (existingUser && existingUser.lastname) || '',
    email: (candidateUser && candidateUser.email) || (existingUser && existingUser.email) || '',
    phone: (candidateUser && candidateUser.phone) || (existingUser && existingUser.phone) || '',
    Roles: candidateRoles.length >= existingRoles.length ? candidateRoles : existingRoles,
    access_users: [...new Set([...existingAccessUsers, ...candidateAccessUsers].filter(Boolean))],
  };

  const existingScore = scoreAssociatedUserCompleteness(existingUser);
  const candidateScore = scoreAssociatedUserCompleteness(candidateUser);

  if (candidateScore >= existingScore) {
    return mergedUser;
  }

  return {
    ...mergedUser,
    ...(existingUser || {}),
    user_id: (existingUser && existingUser.user_id) || mergedUser.user_id,
    auth0_id: (existingUser && existingUser.auth0_id) || mergedUser.auth0_id,
    username: (existingUser && existingUser.username) || mergedUser.username,
    firstname: (existingUser && existingUser.firstname) || mergedUser.firstname,
    lastname: (existingUser && existingUser.lastname) || mergedUser.lastname,
    email: (existingUser && existingUser.email) || mergedUser.email,
    phone: (existingUser && existingUser.phone) || mergedUser.phone,
    Roles: existingRoles.length >= candidateRoles.length ? existingRoles : candidateRoles,
  };
};

const dedupeAssociatedUsers = users =>
  sortUsersByName(
    (Array.isArray(users) ? users : [])
      .filter(Boolean)
      .reduce((dedupedUsers, candidateUser) => {
        const dedupeKey = getAssociatedUserDedupeKey(candidateUser) || null;

        if (!dedupeKey) {
          dedupedUsers.push(candidateUser);
          return dedupedUsers;
        }

        const existingUserIndex = dedupedUsers.findIndex(existingUser => getAssociatedUserDedupeKey(existingUser) === dedupeKey);

        if (existingUserIndex === -1) {
          dedupedUsers.push(candidateUser);
          return dedupedUsers;
        }

        dedupedUsers[existingUserIndex] = mergeAssociatedUsers(dedupedUsers[existingUserIndex], candidateUser);

        return dedupedUsers;
      }, [])
  );

const normalizeAdminUserRecord = candidateUser => {
  if (!candidateUser || typeof candidateUser !== 'object') {
    return null;
  }

  const roles = Array.isArray(candidateUser.Roles)
    ? candidateUser.Roles
    : (Array.isArray(candidateUser.roles) ? candidateUser.roles : []);

  const normalizedRoles = roles
    .filter(Boolean)
    .map(roleRecord => ({
      role_id: (roleRecord && (roleRecord.role_id || roleRecord.roleId)) || '',
      role_description: (roleRecord && (roleRecord.role_description || roleRecord.roleDescription)) || '',
      role: Number.isInteger(roleRecord && roleRecord.role)
        ? roleRecord.role
        : Number(roleRecord && roleRecord.role) || 220,
    }));

  const normalizedUser = {
    user_id: candidateUser.user_id || candidateUser.userId || '',
    auth0_id: candidateUser.auth0_id || candidateUser.auth0Id || '',
    username: candidateUser.username || candidateUser.user_name || candidateUser.userName || candidateUser.email || '',
    firstname: candidateUser.firstname || candidateUser.first_name || candidateUser.firstName || '',
    lastname: candidateUser.lastname || candidateUser.last_name || candidateUser.lastName || '',
    email: candidateUser.email || '',
    phone: candidateUser.phone || candidateUser.phone_number || candidateUser.phoneNumber || '',
    store_id: candidateUser.store_id || candidateUser.storeId || '',
    access_users: Array.isArray(candidateUser.access_users)
      ? candidateUser.access_users
      : (Array.isArray(candidateUser.accessUsers) ? candidateUser.accessUsers : []),
    Roles: normalizedRoles.length > 0 ? normalizedRoles : [{ role_id: '', role_description: 'store_user', role: 220 }],
  };

  if (!normalizedUser.user_id && !normalizedUser.auth0_id && !normalizedUser.email) {
    return null;
  }

  return normalizedUser;
};

const normalizeAdminUsersDataset = users =>
  (Array.isArray(users) ? users : [])
    .map(normalizeAdminUserRecord)
    .filter(Boolean);

const mapLinkedMerchantsToAssociatedUsers = linkedMerchants =>
  (Array.isArray(linkedMerchants) ? linkedMerchants : [])
    .filter(Boolean)
    .map(candidateUser => ({
      auth0_id: candidateUser.auth0_id || '',
      user_id: candidateUser.user_id || '',
      username: candidateUser.username || candidateUser.email || '',
      firstname: candidateUser.firstname || '',
      lastname: candidateUser.lastname || '',
      email: candidateUser.email || '',
      phone: candidateUser.phone || '',
      access_users: Array.isArray(candidateUser.access_users) ? candidateUser.access_users : [],
      Roles: Array.isArray(candidateUser.roles)
        ? candidateUser.roles.map(roleRecord => ({
            role_id: '',
            role_description: roleRecord && roleRecord.role_description ? roleRecord.role_description : '',
            role: roleRecord && Number.isInteger(roleRecord.role) ? roleRecord.role : 220,
          }))
        : [{ role_id: '', role_description: 'store_user', role: 220 }]
    }));

const resolveStoreAccessLinkedUsersForStore = ({ payload, storeId }) => {
  if (!payload || typeof payload !== 'object' || !storeId) {
    return [];
  }

  const selectedStoreId = String(storeId);
  const payloadStoreIds = Array.isArray(payload.store_ids)
    ? payload.store_ids.map(candidateStoreId => String(candidateStoreId))
    : [];
  const linkedUsers = mapLinkedMerchantsToAssociatedUsers(payload.linked_merchants);

  if (payloadStoreIds.includes(selectedStoreId)) {
    return linkedUsers;
  }

  return linkedUsers.filter(candidateUser => String(candidateUser && candidateUser.store_id ? candidateUser.store_id : '') === selectedStoreId);
};

const safeFormatPhone = phoneNumber => {
  if (!phoneNumber) {
    return '';
  }

  return formatPhone(phoneNumber);
};

const normalizeStoreDirectType = directType => {
  const directTypeValues = Array.isArray(directType) ? directType : [];

  return directTypeValues.includes('multidrop') || directTypeValues.includes('waypoint') ? ['multidrop'] : ['regular'];
};

const getDeliveryTypeValue = directType => normalizeStoreDirectType(directType)[0];

const getDeliveryTypeLabel = directType => {
  const deliveryTypeValue = getDeliveryTypeValue(directType);
  const configuredOption = DELIVERY_TYPE_OPTIONS.find(option => option.value === deliveryTypeValue);

  return configuredOption ? configuredOption.label : 'Regular';
};

const STORE_BASE_DETAIL_QUERY_FIELDS = [
  'store_id',
  'name',
  'phone',
  'address_1',
  'address_2',
  'city',
  'state',
  'zip',
  'timezone',
  'direct_type',
  'is_enabled',
  'is_individual_user'
];

const buildStoreBaseDetailQuery = storeId => {
  return `{Stores(where: {store_id: {_eq: "${storeId}"}}) {
    ${STORE_BASE_DETAIL_QUERY_FIELDS.join('\n    ')}
    StoreOwnerships {
      firstname
      lastname
      email
      merchant_id
    }
  }}`;
};

const buildStoreTimesQuery = storeId => `{
  Stores(where: {store_id: {_eq: "${storeId}"}}) {
    store_id
    StoreTimes(order_by: {day_of_week: asc}) {
      day_of_week
      open_time
      close_time
      is_closed
    }
  }
}`;

const getGraphqlErrorMessage = response => {
  const graphqlErrors = response && response.data && Array.isArray(response.data.errors)
    ? response.data.errors
    : [];

  if (graphqlErrors.length === 0) {
    return '';
  }

  const firstError = graphqlErrors[0];
  return firstError && typeof firstError.message === 'string' && firstError.message.trim() !== ''
    ? firstError.message
    : 'GraphQL request failed.';
};

const buildStoreForm = store => ({
  name: (store && store.name) || '',
  phone: (store && store.phone) || '',
  address_1: (store && store.address_1) || '',
  address_2: (store && store.address_2) || '',
  city: (store && store.city) || '',
  state: (store && store.state) || '',
  zip: (store && store.zip) || '',
  timezone: (store && store.timezone) || '',
  direct_type: normalizeStoreDirectType(store && store.direct_type)
});

const buildStoreUpdatePayload = ({ store, storeForm, updatedBy }) => {
  const currentStore = store || {};
  const nextDirectType = normalizeStoreDirectType(storeForm && storeForm.direct_type);
  return {
    name: (storeForm && storeForm.name) || '',
    phone: (storeForm && storeForm.phone) || '',
    logo: currentStore.logo || '',
    address_1: (storeForm && storeForm.address_1) || '',
    address_2: (storeForm && storeForm.address_2) || '',
    city: (storeForm && storeForm.city) || '',
    state: (storeForm && storeForm.state) || '',
    zip: (storeForm && storeForm.zip) || '',
    country: currentStore.country || 'United States',
    latitude: currentStore.latitude,
    longitude: currentStore.longitude,
    timezone: (storeForm && storeForm.timezone) || currentStore.timezone || '',
    direct_type: nextDirectType,
    updated_by: updatedBy || currentStore.updated_by || '',
    default_bridge_vehicle_type: currentStore.default_bridge_vehicle_type || 'suv'
  };
};

const buildUserForm = user => ({
  firstname: (user && user.firstname) || '',
  lastname: (user && user.lastname) || '',
  email: (user && user.email) || '',
  phone_number: (user && user.phone) || '',
  role: user && user.Roles && user.Roles[0] && user.Roles[0].role ? String(user.Roles[0].role) : ''
});

const getUserPrimaryRole = user => (user && user.Roles && user.Roles[0] ? user.Roles[0] : null);

const getUserRoleLabel = user => {
  const primaryRole = getUserPrimaryRole(user);

  if (!primaryRole) {
    return 'N/A';
  }

  const configuredRole = ASSOCIATED_USER_ROLE_OPTIONS.find(option => option.value === String(primaryRole.role));
  if (configuredRole) {
    return configuredRole.label;
  }

  if (primaryRole.role_description) {
    return primaryRole.role_description
      .replace(/_/g, ' ')
      .replace(/(^|\s)\S/g, character => character.toUpperCase());
  }

  return 'N/A';
};

const getAssignableUserRoleOptions = currentRole => {
  const parsedRole = Number(currentRole);

  if (Number.isNaN(parsedRole)) {
    return [];
  }

  return ASSOCIATED_USER_ROLE_OPTIONS;
};

const getEditableUserRoleOptions = (currentRole, user) => {
  const options = getAssignableUserRoleOptions(currentRole);
  const primaryRole = getUserPrimaryRole(user);

  if (!primaryRole) {
    return options;
  }

  const currentRoleValue = String(primaryRole.role);
  if (options.some(option => option.value === currentRoleValue)) {
    return options;
  }

  return [{ value: currentRoleValue, label: getUserRoleLabel(user) }, ...options];
};

const getPrimaryStoreOwner = store => {
  if (!store || !store.StoreOwnerships || store.StoreOwnerships.length === 0) {
    return null;
  }

  return store.StoreOwnerships[0];
};

const getFallbackAccountingEmail = store => {
  const primaryOwner = getPrimaryStoreOwner(store);

  return (primaryOwner && primaryOwner.email) || '';
};

const getFallbackAccountingEmailFromUsers = associatedUsers => {
  if (!Array.isArray(associatedUsers) || associatedUsers.length === 0) {
    return '';
  }

  const firstUserWithEmail = associatedUsers.find(user => user && user.email);

  return (firstUserWithEmail && firstUserWithEmail.email) || '';
};

const hasStoreFormChanges = (store, storeForm) => {
  if (!store) {
    return false;
  }

  const normalizedStoreDirectType = getDeliveryTypeValue(store.direct_type);
  const normalizedFormDirectType = getDeliveryTypeValue(storeForm.direct_type);

  return (
    (store.name || '') !== (storeForm.name || '') ||
    (store.phone || '') !== (storeForm.phone || '') ||
    (store.address_1 || '') !== (storeForm.address_1 || '') ||
    (store.address_2 || '') !== (storeForm.address_2 || '') ||
    (store.city || '') !== (storeForm.city || '') ||
    (store.state || '') !== (storeForm.state || '') ||
    (store.zip || '') !== (storeForm.zip || '') ||
    (store.timezone || '') !== (storeForm.timezone || '') ||
    normalizedStoreDirectType !== normalizedFormDirectType
  );
};

const getRequestHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + getPreferredAuthToken()
});

const getInventoryHasuraAdminSecret = () =>
  (config.config.api_inventory &&
    config.config.api_inventory.admin &&
    (config.config.api_inventory.admin.inventoryHasuraAdminSecret || config.config.api_inventory.admin.InventoryHasuraAdminSecret)) ||
  '';

const hasInventoryHasuraAuthError = graphqlErrors => {
  const errors = Array.isArray(graphqlErrors) ? graphqlErrors : [];

  return errors.some(errorRecord => {
    const code = errorRecord && errorRecord.extensions ? errorRecord.extensions.code : '';
    const message = errorRecord && typeof errorRecord.message === 'string' ? errorRecord.message.toLowerCase() : '';

    return code === 'invalid-jwt' || message.includes('malformed authorization header') || message.includes('invalid-jwt');
  });
};

const shouldRetryInventoryHasuraWithAdminSecret = error => {
  if (!isDashboardLocalDebugMode() || !getInventoryHasuraAdminSecret()) {
    return false;
  }

  if (!error || !error.response) {
    return false;
  }

  const statusCode = error.response.status;

  if ([401, 403].includes(statusCode)) {
    return true;
  }

  return hasInventoryHasuraAuthError(error.response.data && error.response.data.errors);
};

const postInventoryHasuraQuery = async query => {
  const requestWithHeaders = requestHeaders =>
    axios({
      method: 'post',
      url: inventory_urls.hasuraHost,
      timeout: MERCHANT_LIST_REQUEST_TIMEOUT_MS,
      headers: {
        'content-type': 'application/json',
        ...requestHeaders
      },
      data: {
        query
      }
    });

  try {
    const response = await requestWithHeaders({
      Authorization: 'Bearer ' + getPreferredAuthToken()
    });

    if (hasInventoryHasuraAuthError(response && response.data && response.data.errors) && getInventoryHasuraAdminSecret() && isDashboardLocalDebugMode()) {
      return requestWithHeaders({
        'x-hasura-admin-secret': getInventoryHasuraAdminSecret()
      });
    }

    return response;
  } catch (error) {
    if (!shouldRetryInventoryHasuraWithAdminSecret(error)) {
      throw error;
    }

    return requestWithHeaders({
      'x-hasura-admin-secret': getInventoryHasuraAdminSecret()
    });
  }
};

const getCanonicalPaymentsHost = () => {
  const configuredPaymentsHost = config.config.api_payments.host;

  if (!configuredPaymentsHost) {
    return 'https://dev-api-payments.gophr.app';
  }

  if (isLocalLikeHost(configuredPaymentsHost)) {
    return config.config.AUTH_CONFIG && config.config.AUTH_CONFIG.domain === 'gophr.auth0.com'
      ? 'https://api-payments.gophr.app'
      : 'https://dev-api-payments.gophr.app';
  }

  return configuredPaymentsHost;
};

const getResponsePayload = response => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  if (response.data && typeof response.data === 'object' && response.data.payload != null) {
    return response.data.payload;
  }

  if (response.data != null) {
    return response.data;
  }

  return null;
};

const getPaymentProfilePayload = paymentProfileResponse => {
  if (!paymentProfileResponse || typeof paymentProfileResponse !== 'object') {
    return null;
  }

  if (paymentProfileResponse.payload && typeof paymentProfileResponse.payload === 'object') {
    return paymentProfileResponse.payload;
  }

  return paymentProfileResponse;
};

const getKnownInvoiceFrequency = (...invoiceFrequencies) => {
  for (const invoiceFrequency of invoiceFrequencies) {
    const normalizedInvoiceFrequency = normalizeInvoiceFrequency(invoiceFrequency);

    if (normalizedInvoiceFrequency && normalizedInvoiceFrequency !== 'n/a') {
      return normalizedInvoiceFrequency;
    }
  }

  return 'single';
};

const buildPaymentProfileState = ({ billingEmail = '', invoiceFrequency, fallbackInvoiceFrequency } = {}) => ({
  billing_email: billingEmail || '',
  invoice_frequency: getKnownInvoiceFrequency(invoiceFrequency, fallbackInvoiceFrequency)
});

const getRequestErrorMessage = (error, fallbackMessage) => {
  if (error && error.response && error.response.data) {
    const responseData = error.response.data;

    if (typeof responseData.message === 'string' && responseData.message.trim() !== '') {
      return responseData.message;
    }

    if (typeof responseData.error === 'string' && responseData.error.trim() !== '') {
      return responseData.error;
    }

    if (typeof responseData === 'object') {
      try {
        return JSON.stringify(responseData);
      } catch (stringifyError) {
        return fallbackMessage;
      }
    }
  }

  return (error && error.message) || fallbackMessage;
};

const persistStoreDirectType = async ({ storeId, directType }) => {
  if (!storeId || !inventory_urls.hasuraHost || isLocalLikeHost(inventory_urls.hasuraHost)) {
    return;
  }

  const mutation = {
    query: `mutation UpdateStoreDirectType($storeId: String!, $directType: [String!]!) {
      update_Stores(where: {store_id: {_eq: $storeId}}, _set: {direct_type: $directType}) {
        affected_rows
      }
    }`,
    variables: {
      storeId,
      directType
    }
  };

  const authorizationHeaders = getRequestHeaders();
  const inventoryHasuraAdminSecret =
    (config.config.api_inventory &&
      config.config.api_inventory.admin &&
      (config.config.api_inventory.admin.inventoryHasuraAdminSecret || config.config.api_inventory.admin.InventoryHasuraAdminSecret)) ||
    '';

  try {
    const response = await axios({
      method: 'post',
      url: inventory_urls.hasuraHost,
      headers: authorizationHeaders,
      data: mutation
    });

    const affectedRows =
      response && response.data && response.data.data && response.data.data.update_Stores ? response.data.data.update_Stores.affected_rows : 0;

    if (affectedRows > 0) {
      return;
    }
  } catch (authorizationError) {
    if (!inventoryHasuraAdminSecret) {
      throw authorizationError;
    }
  }

  if (!inventoryHasuraAdminSecret) {
    throw new Error('Unable to persist delivery type.');
  }

  const fallbackResponse = await axios({
    method: 'post',
    url: inventory_urls.hasuraHost,
    headers: {
      'content-type': 'application/json',
      'x-hasura-admin-secret': inventoryHasuraAdminSecret
    },
    data: mutation
  });

  const fallbackAffectedRows =
    fallbackResponse && fallbackResponse.data && fallbackResponse.data.data && fallbackResponse.data.data.update_Stores
      ? fallbackResponse.data.data.update_Stores.affected_rows
      : 0;

  if (fallbackAffectedRows <= 0) {
    throw new Error('Unable to persist delivery type.');
  }
};

const decodeJwtPayload = token => {
  if (!token) {
    return null;
  }

  try {
    const payloadSegment = token.split('.')[1];

    if (!payloadSegment) {
      return null;
    }

    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const normalizedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = window.atob(normalizedBase64);

    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

const isJwtLikeToken = token => typeof token === 'string' && token.split('.').length === 3;

const getPreferredAuthToken = () => {
  const accessToken = localStorage.getItem('access_token') || '';
  const idToken = localStorage.getItem('id_token') || '';

  // Backend APIs expect an access token audience; prefer access_token whenever available.
  if (accessToken) {
    return accessToken;
  }

  const tokenCandidates = [idToken].filter(isJwtLikeToken);

  if (tokenCandidates.length > 0) {
    return tokenCandidates[0];
  }

  return idToken || '';
};

const normalizeJwtIdentityValue = value => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
};

const escapeGraphqlString = value => String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const getCurrentMerchantIdentity = () => {
  const accessTokenPayload = decodeJwtPayload(localStorage.getItem('access_token')) || null;
  const idTokenPayload = decodeJwtPayload(localStorage.getItem('id_token')) || null;

  return {
    auth0Id: normalizeJwtIdentityValue(
      (accessTokenPayload && accessTokenPayload.sub)
      || (idTokenPayload && idTokenPayload.sub)
    ),
    email: normalizeJwtIdentityValue(
      (idTokenPayload && idTokenPayload.email)
      || (accessTokenPayload && accessTokenPayload.email)
    ).toLowerCase(),
    username: normalizeJwtIdentityValue(
      (idTokenPayload && idTokenPayload.nickname)
      || (idTokenPayload && idTokenPayload.username)
      || (accessTokenPayload && accessTokenPayload.nickname)
      || (accessTokenPayload && accessTokenPayload.username)
    ).toLowerCase()
  };
};

const getCurrentAuth0Id = () => {
  const payload = decodeJwtPayload(localStorage.getItem('access_token')) || decodeJwtPayload(localStorage.getItem('id_token'));

  return (payload && payload.sub) || '';
};

const getAdminEditUserUrl = userAuth0Id => {
  const configuredUsersHost = config.config.api_users.admin.usersHost;
  const shouldUseRemoteUsersHost = isDashboardLocalDebugMode() && isLocalLikeHost(configuredUsersHost);

  const usersHost = shouldUseRemoteUsersHost ? 'https://dev-api-users.gophr.app' : configuredUsersHost;

  return usersHost + '/admin/edit/' + encodeURIComponent(userAuth0Id);
};

const getMerchantEditUserUrl = userAuth0Id => merchant_urls.editMerchant + encodeURIComponent(userAuth0Id);

const getResolvedAdminUsersHosts = () => {
  const configuredUsersHost = config.config.api_users.admin.usersHost;
  const isLocalDashboard = isDashboardLocalDebugMode();
  const localUsersHost = 'http://localhost:8082';
  const prodUsersHost = 'https://api-users.gophr.app';
  const hostedUsersHost = 'https://dev-api-users.gophr.app';
  const useHostedOnly = isLocalDashboard
    && isLocalLikeHost(configuredUsersHost)
    && config.config.AUTH_CONFIG
    && config.config.AUTH_CONFIG.domain === 'gophr.auth0.com';

  if (useHostedOnly) {
    return [...new Set([hostedUsersHost, prodUsersHost, getCanonicalUsersHost(), localUsersHost].filter(Boolean))];
  }

  if (isLocalDashboard && isLocalLikeHost(configuredUsersHost)) {
    return [...new Set([configuredUsersHost, localUsersHost, hostedUsersHost].filter(Boolean))];
  }

  if (isLocalDashboard) {
    return [...new Set([configuredUsersHost, hostedUsersHost].filter(Boolean))];
  }

  return [configuredUsersHost];
};

const getCanonicalUsersHost = () => {
  const configuredUsersHost = config.config.api_users.admin.usersHost;

  if (!configuredUsersHost) {
    return 'https://dev-api-users.gophr.app';
  }

  if (isLocalLikeHost(configuredUsersHost)) {
    return config.config.AUTH_CONFIG && config.config.AUTH_CONFIG.domain === 'gophr.auth0.com'
      ? 'https://api-users.gophr.app'
      : 'https://dev-api-users.gophr.app';
  }

  return configuredUsersHost;
};

const shouldPreferLocalUsersHostAtRuntime = () => {
  const configuredUsersHost = config.config.api_users.admin.usersHost;

  if (isLocalLikeHost(configuredUsersHost)) {
    return true;
  }

  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  return ['localhost', '127.0.0.1'].includes(String(window.location.hostname || '').toLowerCase());
};

const getAssociatedUsersCandidateHosts = () => {
  const configuredUsersHost = config.config.api_users.admin.usersHost;
  const canonicalUsersHost = getCanonicalUsersHost();
  const localUsersHost = 'http://localhost:8082';
  const prodUsersHost = 'https://api-users.gophr.app';
  const devUsersHost = 'https://dev-api-users.gophr.app';
  const hostedUsersHost = user_urls && user_urls.hostedUsersHost ? user_urls.hostedUsersHost : '';
  const useHostedOnly = isDashboardLocalDebugMode()
    && isLocalLikeHost(configuredUsersHost)
    && config.config.AUTH_CONFIG
    && config.config.AUTH_CONFIG.domain === 'gophr.auth0.com';

  if (useHostedOnly) {
    return [...new Set([localUsersHost, devUsersHost, prodUsersHost, canonicalUsersHost, hostedUsersHost].filter(Boolean))];
  }

  if (shouldPreferLocalUsersHostAtRuntime()) {
    return [...new Set([localUsersHost, configuredUsersHost, canonicalUsersHost, hostedUsersHost, devUsersHost, prodUsersHost].filter(Boolean))];
  }

  if (isDashboardLocalDebugMode()) {
    return [...new Set([localUsersHost, configuredUsersHost, canonicalUsersHost, hostedUsersHost].filter(Boolean))];
  }

  return [...new Set([configuredUsersHost, canonicalUsersHost, hostedUsersHost].filter(Boolean))];
};

const fetchAdminUsersByStoreAcrossHosts = async storeId => {
  const candidateHosts = getAssociatedUsersCandidateHosts();
  const getUsersFromPayload = payload => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && Array.isArray(payload.users)) {
      return payload.users;
    }

    return [];
  };

  const hostRequests = candidateHosts.map(usersHost =>
    axios({
      method: 'get',
      url: usersHost + '/admin/users',
      withCredentials: false,
      timeout: MERCHANT_LIST_REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: 'Bearer ' + getPreferredAuthToken(),
        'content-type': 'application/json'
      }
    }).then(response => ({
      usersHost,
      users: normalizeAdminUsersDataset(getUsersFromPayload(getResponsePayload(response)))
    }))
  );

  try {
    const firstStoreMatch = await Promise.any(
      hostRequests.map(request =>
        request.then(candidateResult => {
          const users = Array.isArray(candidateResult.users) ? candidateResult.users : [];
          const hasStoreMatch = users.some(candidateUser => String(candidateUser && candidateUser.store_id ? candidateUser.store_id : '') === String(storeId));

          if (!hasStoreMatch) {
            throw new Error('No store users in this host response.');
          }

          return candidateResult;
        })
      )
    );

    return {
      users: Array.isArray(firstStoreMatch.users) ? firstStoreMatch.users : [],
      sourceHost: firstStoreMatch.usersHost || '',
    };
  } catch (error) {
    void error;
  }

  const responses = await Promise.allSettled(hostRequests);

  const successfulResponses = responses
    .filter(candidateResponse => candidateResponse.status === 'fulfilled' && candidateResponse.value)
    .map(candidateResponse => candidateResponse.value);

  if (successfulResponses.length === 0) {
    return {
      users: [],
      sourceHost: '',
    };
  }

  const bestResponse = successfulResponses.reduce((bestResult, candidateResult) => {
    const candidateUsers = Array.isArray(candidateResult.users) ? candidateResult.users : [];
    const bestUsers = Array.isArray(bestResult.users) ? bestResult.users : [];

    return candidateUsers.length > bestUsers.length ? candidateResult : bestResult;
  }, successfulResponses[0]);

  return {
    users: Array.isArray(bestResponse.users) ? bestResponse.users : [],
    sourceHost: bestResponse.usersHost || '',
  };
};

const fetchStorePaymentProfileAcrossHosts = async storeId => {
  try {
    const cascadedStoreProfile = await fetchStorePaymentProfile(storeId, {
      allowAdminFallbackOn404: true,
      merchantAuth0Id: getCurrentAuth0Id(),
    });
    const cascadedPayload = getPaymentProfilePayload(cascadedStoreProfile) || getResponsePayload(cascadedStoreProfile);

    if (cascadedPayload) {
      return {
        payload: cascadedPayload,
        sourceHost: 'users-cascade',
      };
    }
  } catch (error) {
    void error;
  }

  const candidateHosts = getAssociatedUsersCandidateHosts();
  let fallbackPayload = null;
  let fallbackSourceHost = '';
  let bestPayload = null;
  let bestSourceHost = '';
  let bestScore = -1;

  const scorePayload = payload => {
    if (!payload) {
      return -1;
    }

    const merchantAuth0Count = Array.isArray(payload.merchant_auth0_ids)
      ? payload.merchant_auth0_ids.filter(Boolean).length
      : 0;
    const merchantUsersCount = Array.isArray(payload.merchant_users)
      ? payload.merchant_users.filter(Boolean).length
      : 0;

    return Math.max(merchantAuth0Count, merchantUsersCount);
  };

  const responses = await Promise.allSettled(
    candidateHosts.map(usersHost =>
      axios({
        method: 'get',
        url: usersHost + '/admin/store-payment-profile/' + encodeURIComponent(storeId),
        withCredentials: false,
        timeout: MERCHANT_LIST_REQUEST_TIMEOUT_MS,
        headers: {
          Authorization: 'Bearer ' + getPreferredAuthToken(),
          'content-type': 'application/json'
        }
      }).then(response => ({
        usersHost,
        payload: getResponsePayload(response)
      }))
    )
  );

  const successfulResponses = responses
    .filter(candidateResponse => candidateResponse.status === 'fulfilled' && candidateResponse.value)
    .map(candidateResponse => candidateResponse.value);

  successfulResponses.forEach(candidateResult => {
    if (!fallbackPayload && candidateResult.payload) {
      fallbackPayload = candidateResult.payload;
      fallbackSourceHost = candidateResult.usersHost;
    }

    const payloadScore = scorePayload(candidateResult.payload);
    if (payloadScore > bestScore) {
      bestScore = payloadScore;
      bestPayload = candidateResult.payload;
      bestSourceHost = candidateResult.usersHost;
    }
  });

  if (bestPayload) {
    return {
      payload: bestPayload,
      sourceHost: bestSourceHost,
    };
  }

  return {
    payload: fallbackPayload,
    sourceHost: fallbackSourceHost,
  };
};

const normalizeAccessUsers = candidateAccessUsers => {
  if (!Array.isArray(candidateAccessUsers)) {
    return [];
  }

  return [...new Set(candidateAccessUsers
    .map(candidateUserId => (candidateUserId == null ? '' : String(candidateUserId).trim()))
    .filter(Boolean))];
};

const resolveAssociatedUsersFromAdminDataset = ({ adminUsers, storeId, ownershipMerchantAuth0Ids, paymentProfileMerchantAuth0Ids, hasuraUsers }) => {
  const users = Array.isArray(adminUsers) ? adminUsers.filter(Boolean) : [];

  if (users.length === 0) {
    return [];
  }

  const storeIdString = String(storeId || '');
  const seedAuth0Ids = new Set([
    ...((Array.isArray(ownershipMerchantAuth0Ids) ? ownershipMerchantAuth0Ids : []).map(candidateId => String(candidateId))),
    ...((Array.isArray(paymentProfileMerchantAuth0Ids) ? paymentProfileMerchantAuth0Ids : []).map(candidateId => String(candidateId))),
    ...((Array.isArray(hasuraUsers) ? hasuraUsers : [])
      .map(candidateUser => candidateUser && candidateUser.auth0_id ? String(candidateUser.auth0_id) : '')
      .filter(Boolean))
  ]);

  const usersById = new Map();
  const usersByAuth0Id = new Map();
  users.forEach(candidateUser => {
    if (candidateUser && candidateUser.user_id) {
      usersById.set(String(candidateUser.user_id), candidateUser);
    }

    if (candidateUser && candidateUser.auth0_id) {
      usersByAuth0Id.set(String(candidateUser.auth0_id), candidateUser);
    }
  });

  const resolveLinkedUserIds = linkedIdentifiers => {
    const resolvedUserIds = new Set();

    normalizeAccessUsers(linkedIdentifiers).forEach(linkedIdentifier => {
      if (usersById.has(linkedIdentifier)) {
        resolvedUserIds.add(linkedIdentifier);
      }

      const linkedByAuth0 = usersByAuth0Id.get(linkedIdentifier);
      if (linkedByAuth0 && linkedByAuth0.user_id) {
        resolvedUserIds.add(String(linkedByAuth0.user_id));
      }
    });

    return [...resolvedUserIds];
  };

  const selectedUserIds = new Set();

  users.forEach(candidateUser => {
    if (!candidateUser || !candidateUser.user_id) {
      return;
    }

    const candidateUserId = String(candidateUser.user_id);

    if (candidateUser.store_id && String(candidateUser.store_id) === storeIdString) {
      selectedUserIds.add(candidateUserId);
      return;
    }

    if (candidateUser.auth0_id && seedAuth0Ids.has(String(candidateUser.auth0_id))) {
      selectedUserIds.add(candidateUserId);
    }
  });

  let expanded = true;
  while (expanded) {
    expanded = false;

    for (const candidateUser of users) {
      if (!candidateUser || !candidateUser.user_id) {
        continue;
      }

      const candidateUserId = String(candidateUser.user_id);
      const candidateAccessUsers = resolveLinkedUserIds(candidateUser.access_users);
      const isSelectedUser = selectedUserIds.has(candidateUserId);
      const hasLinkToSelected = candidateAccessUsers.some(linkedUserId => selectedUserIds.has(linkedUserId));

      if (hasLinkToSelected && !isSelectedUser) {
        selectedUserIds.add(candidateUserId);
        expanded = true;
      }

      if (isSelectedUser) {
        for (const linkedUserId of candidateAccessUsers) {
          if (!selectedUserIds.has(linkedUserId) && usersById.has(linkedUserId)) {
            selectedUserIds.add(linkedUserId);
            expanded = true;
          }
        }
      }
    }
  }

  const selectedUsers = users
    .filter(candidateUser => candidateUser && candidateUser.user_id && selectedUserIds.has(String(candidateUser.user_id)))
    .filter(candidateUser => Boolean(
      (candidateUser && candidateUser.auth0_id)
      || (candidateUser && candidateUser.email)
      || (candidateUser && candidateUser.user_id)
    ));

  return sortUsersByName(selectedUsers);
};
const extractUsersFromHasuraResponse = response => {
  const usersErrorMessage = getGraphqlErrorMessage(response);

  if (usersErrorMessage) {
    return [];
  }

  return response && response.data && response.data.data && Array.isArray(response.data.data.Users)
    ? response.data.data.Users
    : [];
};

const fetchUsersByStoreAcrossHasuraHosts = async storeId => {
  const normalizedStoreId = String(storeId || '').trim();

  if (!normalizedStoreId) {
    return {
      users: [],
      sourceHost: '',
      authMode: '',
    };
  }

  const hasuraHosts = [...new Set([
    '/__local/users-hasura',
    user_urls.hasuraHost,
    'https://api-users.hasura.gophr.app/v1/graphql',
    'https://dev-api-users.hasura.gophr.app/v1/graphql'
  ].filter(Boolean))];
  const usersHasuraAdminSecret = config.config.api_users.admin.usersHasuraAdminSecret;
  const accessToken = localStorage.getItem('access_token') || '';
  const idToken = localStorage.getItem('id_token') || '';
  const bearerTokenCandidates = [...new Set([accessToken, idToken, getPreferredAuthToken()].filter(Boolean))];
  const escapedStoreId = escapeGraphqlString(normalizedStoreId);
  const query = `{Users(where: {store_id: {_eq: "${escapedStoreId}"}}, order_by: [{firstname: asc}, {lastname: asc}]) {
      user_id
      auth0_id
      username
      firstname
      lastname
      email
      phone
      store_id
    }}`;

  const normalizeStoreUsers = users =>
    (Array.isArray(users) ? users : [])
      .filter(candidateUser => candidateUser && (candidateUser.auth0_id || candidateUser.email || candidateUser.user_id))
      .map(candidateUser => ({
        user_id: candidateUser.user_id || '',
        auth0_id: candidateUser.auth0_id || '',
        username: candidateUser.username || candidateUser.email || '',
        firstname: candidateUser.firstname || '',
        lastname: candidateUser.lastname || '',
        email: candidateUser.email || '',
        phone: candidateUser.phone || '',
        store_id: candidateUser.store_id || normalizedStoreId,
        access_users: [],
        Roles: [{ role_id: '', role_description: 'store_user', role: 220 }]
      }));

  const fetchWithHeaders = async (host, headers, authMode) => {
    const response = await axios({
      method: 'post',
      url: host,
      timeout: MERCHANT_LIST_REQUEST_TIMEOUT_MS,
      headers,
      data: { query }
    });
    const users = normalizeStoreUsers(extractUsersFromHasuraResponse(response));

    if (users.length === 0) {
      throw new Error('No users found for store on this host/auth combination.');
    }

    return {
      users,
      sourceHost: host,
      authMode,
    };
  };

  for (const host of hasuraHosts) {
    if (host === '/__local/users-hasura') {
      try {
        return await fetchWithHeaders(host, {
          'content-type': 'application/json'
        }, 'local-proxy-admin');
      } catch (localProxyError) {
        void localProxyError;
      }
      continue;
    }

    if (usersHasuraAdminSecret) {
      try {
        return await fetchWithHeaders(host, {
          'content-type': 'application/json',
          'x-hasura-admin-secret': usersHasuraAdminSecret
        }, 'admin-secret');
      } catch (adminSecretError) {
        void adminSecretError;
      }
    }

    if (bearerTokenCandidates.length > 0) {
      for (const bearerToken of bearerTokenCandidates) {
        try {
          return await fetchWithHeaders(host, {
            'content-type': 'application/json',
            Authorization: 'Bearer ' + bearerToken
          }, bearerToken === accessToken ? 'bearer-access' : 'bearer-id');
        } catch (bearerError) {
          void bearerError;
        }
      }
    }
  }

  return {
    users: [],
    sourceHost: '',
    authMode: '',
  };
};

const buildMerchantStoreAccessPath = auth0Id => '/admin/internal/merchants/' + encodeURIComponent(auth0Id) + '/store-access';

const isLocalStoreAccessUrl = url => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return /https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(url);
};

const buildStoreAccessRequestConfig = (url, timeoutMs) => {
  const baseConfig = {
    method: 'get',
    url,
    withCredentials: false,
    timeout: timeoutMs,
  };

  if (isLocalStoreAccessUrl(url)) {
    return baseConfig;
  }

  return {
    ...baseConfig,
    headers: {
      Authorization: 'Bearer ' + getPreferredAuthToken(),
      'content-type': 'application/json'
    }
  };
};

const getMerchantStoreAccessCandidateUrls = auth0Id => {
  const urls = [];
  const merchantStoreAccessPath = buildMerchantStoreAccessPath(auth0Id);
  const canonicalUsersHost = getCanonicalUsersHost();
  const localUsersHost = 'http://localhost:8082';
  const allowHostedFallbackInLocalDebug = process.env.REACT_APP_LOCAL_DEBUG_ALLOW_HOSTED_STORE_ACCESS === 'true';
  const localDebugOnlyMode = isDashboardLocalDebugMode() && !allowHostedFallbackInLocalDebug;

  if (shouldPreferLocalUsersHostAtRuntime() || isDashboardLocalDebugMode()) {
    urls.push(localUsersHost + merchantStoreAccessPath);
  }

  if (typeof user_urls.merchantStoreAccess === 'function') {
    const localOrConfiguredUrl = user_urls.merchantStoreAccess(auth0Id);

    if (localOrConfiguredUrl && (!localDebugOnlyMode || isLocalStoreAccessUrl(localOrConfiguredUrl))) {
      urls.push(localOrConfiguredUrl);

      if (typeof user_urls.toHostedUsersUrl === 'function') {
        const hostedUrl = user_urls.toHostedUsersUrl(localOrConfiguredUrl);

        if (!localDebugOnlyMode) {
          urls.push(hostedUrl);
        }
      }
    }
  }

  if (canonicalUsersHost && !localDebugOnlyMode) {
    urls.push(canonicalUsersHost + merchantStoreAccessPath);
  }

  return [...new Set(urls.filter(Boolean))];
};

const fetchMerchantStoreIdFromAccessEndpoint = async auth0Id => {
  const candidateUrls = getMerchantStoreAccessCandidateUrls(auth0Id);

  if (candidateUrls.length === 0) {
    return '';
  }

  const requests = candidateUrls.map(url =>
    axios(buildStoreAccessRequestConfig(url, MERCHANT_STORE_ACCESS_FAST_TIMEOUT_MS)).then(response => {
      const payload = getResponsePayload(response) || {};
      const resolvedStoreId = payload.store_id
        || (Array.isArray(payload.store_ids) && payload.store_ids.length > 0 ? payload.store_ids[0] : '');

      if (!resolvedStoreId) {
        throw new Error('No store id in store-access payload.');
      }

      return String(resolvedStoreId);
    })
  );

  try {
    return await Promise.any(requests);
  } catch (error) {
    return '';
  }
};

const fetchMerchantStoreAccessPayloadAcrossHosts = async auth0Id => {
  const candidateUrls = getMerchantStoreAccessCandidateUrls(auth0Id);

  if (candidateUrls.length === 0) {
    return null;
  }

  const isUsefulPayload = payload => {
    if (!payload || typeof payload !== 'object') {
      return false;
    }

    const merchantAuth0IdsCount = Array.isArray(payload.merchant_auth0_ids) ? payload.merchant_auth0_ids.filter(Boolean).length : 0;
    const linkedMerchantsCount = Array.isArray(payload.linked_merchants) ? payload.linked_merchants.filter(Boolean).length : 0;
    const storeIdsCount = Array.isArray(payload.store_ids) ? payload.store_ids.filter(Boolean).length : 0;

    return merchantAuth0IdsCount > 0 || linkedMerchantsCount > 0 || storeIdsCount > 0;
  };

  const candidateRequests = candidateUrls.map(url =>
    axios(buildStoreAccessRequestConfig(url, MERCHANT_LIST_REQUEST_TIMEOUT_MS)).then(response => {
      const payload = getResponsePayload(response);

      if (!isUsefulPayload(payload)) {
        throw new Error('Store-access payload was empty.');
      }

      return payload;
    })
  );

  try {
    return await Promise.any(candidateRequests);
  } catch (error) {
    void error;
  }

  const responses = await Promise.allSettled(
    candidateRequests.map(request => request.catch(() => null))
  );

  const successfulPayloads = responses
    .filter(candidateResponse => candidateResponse.status === 'fulfilled' && candidateResponse.value)
    .map(candidateResponse => candidateResponse.value);

  if (successfulPayloads.length === 0) {
    return null;
  }

  const scorePayload = payload => {
    if (!payload || typeof payload !== 'object') {
      return -1;
    }

    const merchantAuth0IdsCount = Array.isArray(payload.merchant_auth0_ids) ? payload.merchant_auth0_ids.filter(Boolean).length : 0;
    const linkedMerchantsCount = Array.isArray(payload.linked_merchants) ? payload.linked_merchants.filter(Boolean).length : 0;
    const storeIdsCount = Array.isArray(payload.store_ids) ? payload.store_ids.filter(Boolean).length : 0;

    return (merchantAuth0IdsCount * 100) + (linkedMerchantsCount * 10) + storeIdsCount;
  };

  return successfulPayloads.reduce((bestPayload, candidatePayload) => {
    if (!bestPayload) {
      return candidatePayload;
    }

    return scorePayload(candidatePayload) > scorePayload(bestPayload) ? candidatePayload : bestPayload;
  }, null);
};

const getAdminStorePaymentProfileUrls = storeId =>
  getResolvedAdminUsersHosts().map(usersHost => usersHost + '/admin/store-payment-profile/' + encodeURIComponent(storeId));

const getWritableStorePaymentProfileUrls = storeId =>
  getResolvedAdminUsersHosts().map(usersHost => usersHost + '/payment/store-payment-profile/' + encodeURIComponent(storeId));

const getAdminMerchantPaymentProfileUrls = merchantAuth0Id =>
  getResolvedAdminUsersHosts().map(usersHost => usersHost + '/admin/merchant-payment-profile/' + encodeURIComponent(merchantAuth0Id));

const isLocalDashboardEnvironment = () =>
  isDashboardLocalDebugMode();

const putViaCandidateUrls = async ({ urls, data, stopOnHttpResponse = false }) => {
  let lastError = null;

  for (let index = 0; index < urls.length; index += 1) {
    const url = urls[index];

    try {
      const response = await axios({
        method: 'PUT',
        url,
        headers: getRequestHeaders(),
        data,
      });

      return response;
    } catch (error) {
      lastError = error;

      if (stopOnHttpResponse && error && error.response) {
        throw error;
      }
    }
  }

  throw lastError || new Error('Unable to save payment profile.');
};

const updateAssociatedUserRole = async ({ roleId, nextRoleValue, roleDescription, updatedBy }) => {
  const response = await axios({
    method: 'post',
    url: user_urls.hasuraHost,
    headers: getRequestHeaders(),
    data: {
      query: `mutation UpdateAssociatedUserRole($roleId: uuid!, $role: Int!, $roleDescription: String!, $updatedBy: String!) {
        update_Roles_by_pk(
          pk_columns: { role_id: $roleId }
          _set: { role: $role, role_description: $roleDescription, updated_by: $updatedBy }
        ) {
          role_id
          role
          role_description
        }
      }`,
      variables: {
        roleId,
        role: nextRoleValue,
        roleDescription,
        updatedBy
      }
    }
  });

  if (response && response.data && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
    throw new Error(response.data.errors[0].message || 'Unable to update user role.');
  }

  const updatedRole = response && response.data && response.data.data && response.data.data.update_Roles_by_pk;

  if (!updatedRole) {
    throw new Error('Unable to update user role.');
  }

  return updatedRole;
};

const getMerchantAuth0Ids = ({ primaryOwner, associatedUsers, isAdminView }) => {
  const auth0Ids = [];

  if (primaryOwner && primaryOwner.merchant_id) {
    auth0Ids.push(primaryOwner.merchant_id);
  }

  if (Array.isArray(associatedUsers)) {
    associatedUsers.forEach(user => {
      if (
        user &&
        user.auth0_id &&
        Array.isArray(user.Roles) &&
        user.Roles.some(roleRecord => roleRecord && roleRecord.role >= 200 && roleRecord.role < 300)
      ) {
        auth0Ids.push(user.auth0_id);
      }
    });
  }

  if (!isAdminView) {
    const currentAuth0Id = getCurrentAuth0Id();

    if (currentAuth0Id) {
      auth0Ids.push(currentAuth0Id);
    }
  }

  return [...new Set(auth0Ids.filter(Boolean))];
};

const resolveMerchantAuth0Id = ({ primaryOwner, associatedUsers, isAdminView }) => {
  return getMerchantAuth0Ids({ primaryOwner, associatedUsers, isAdminView })[0] || '';
};

const resolveStoreOrdersUserIdentifier = ({ primaryOwner, associatedUsers, isAdminView }) => {
  const ownerMerchantAuth0Id = primaryOwner && primaryOwner.merchant_id ? String(primaryOwner.merchant_id) : '';

  if (ownerMerchantAuth0Id) {
    return ownerMerchantAuth0Id;
  }

  if (!isAdminView) {
    const currentAuth0Id = getCurrentAuth0Id();

    if (currentAuth0Id) {
      return currentAuth0Id;
    }
  }

  const merchantAuth0Id = resolveMerchantAuth0Id({ primaryOwner, associatedUsers, isAdminView });

  if (merchantAuth0Id) {
    return merchantAuth0Id;
  }

  const fallbackUser = Array.isArray(associatedUsers) ? associatedUsers.find(user => user && (user.auth0_id || user.email)) : null;

  if (fallbackUser) {
    return fallbackUser.auth0_id || fallbackUser.email || '';
  }

  return (primaryOwner && primaryOwner.email) || '';
};

const buildStoreOrdersUserInfoPath = (userIdentifier, storeId) => {
  const encodedUserIdentifier = encodeURIComponent(userIdentifier);

  if (storeId) {
    return `/admin/users/${encodedUserIdentifier}/store/${encodeURIComponent(storeId)}`;
  }

  return `/admin/users/${encodedUserIdentifier}`;
};
const buildMerchantStoreOrdersUserInfoPath = (matchPath, userIdentifier, storeId) => {
  const encodedUserIdentifier = encodeURIComponent(userIdentifier);

  if (storeId) {
    return `${matchPath}/users/${encodedUserIdentifier}/store/${encodeURIComponent(storeId)}`;
  }

  return `${matchPath}/users/${encodedUserIdentifier}`;
};

const getDirectMerchantPaymentProfileUrl = auth0Id =>
  getCanonicalPaymentsHost() + '/v2/direct/merchant-profiles/' + encodeURIComponent(auth0Id);

const getDirectMerchantPaymentProfilesUrl = () => getCanonicalPaymentsHost() + '/v2/direct/merchant-profiles';

const isDirectProfileRecoverableError = error => {
  const status = error && error.response && error.response.status;

  return [400, 401, 403, 404, 500].includes(status);
};

const isDirectProfileCreateableError = error => {
  const status = error && error.response && error.response.status;

  return status === 404 || status === 500;
};

const getResponseErrorMessage = error => {
  const responseData = error && error.response ? error.response.data : null;

  if (typeof responseData === 'string' && responseData.trim() !== '') {
    return responseData.trim();
  }

  if (responseData && typeof responseData.message === 'string' && responseData.message.trim() !== '') {
    return responseData.message.trim();
  }

  if (responseData && typeof responseData.error === 'string' && responseData.error.trim() !== '') {
    return responseData.error.trim();
  }

  return '';
};

const shouldFallbackToUsersProxy = error => {
  if (!error || !error.response) {
    return true;
  }

  return [404, 500, 502, 503, 504].includes(error.response.status);
};

const isLikelyCorsOrNetworkError = error => {
  if (!error || error.response) {
    return false;
  }

  const message = (error.message || '').toLowerCase();

  return message.includes('network error') || message.includes('failed to fetch') || message.includes('cors');
};

const shouldFallbackFromStorePaymentProfile = error => {
  if (!error || !error.response) {
    return false;
  }

  if (error.response.status === 400) {
    const message = getResponseErrorMessage(error).toLowerCase();

    return message === 'unable to update store payment profile'
      || message === 'no merchant billing profile found for the provided store id';
  }

  return [404, 405, 500, 501, 502, 503, 504].includes(error.response.status);
};

const fetchDirectMerchantPaymentProfile = async merchantAuth0Id => {
  try {
    const response = await axios({
      method: 'GET',
      url: getDirectMerchantPaymentProfileUrl(merchantAuth0Id),
      headers: getRequestHeaders()
    });

    return getResponsePayload(response);
  } catch (error) {
    if (isDirectProfileRecoverableError(error)) {
      return null;
    }

    throw error;
  }
};

const getMerchantIdentity = ({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail }) => {
  const matchingUser = Array.isArray(associatedUsers)
    ? associatedUsers.find(user => user && user.auth0_id === merchantAuth0Id)
    : null;
  const fallbackUser = Array.isArray(associatedUsers) ? associatedUsers.find(user => user && user.email) : null;
  const identitySource = matchingUser || fallbackUser || {};
  const firstName = primaryOwner.firstname || identitySource.firstname || 'Merchant';
  const lastName = primaryOwner.lastname || identitySource.lastname || 'User';
  const email = primaryOwner.email || identitySource.email || billingEmail || '';

  return {
    first_name: firstName,
    last_name: lastName,
    email,
    billing_email: billingEmail || primaryOwner.email || identitySource.email || ''
  };
};

const mergeMerchantIdentityIntoPayload = (payload, existingProfile) => {
  if (!existingProfile) {
    return payload;
  }

  return {
    ...payload,
    first_name: payload.first_name || existingProfile.first_name || 'Merchant',
    last_name: payload.last_name || existingProfile.last_name || 'User',
    email: payload.email || existingProfile.email || existingProfile.billing_email || '',
    billing_email: payload.billing_email || existingProfile.billing_email || existingProfile.email || ''
  };
};

const buildDirectMerchantPaymentProfilePayload = ({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail, invoiceFrequency, storeId, storeName }) => {
  const merchantIdentity = getMerchantIdentity({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail });

  return {
    user_auth0_id: merchantAuth0Id,
    first_name: merchantIdentity.first_name,
    last_name: merchantIdentity.last_name,
    email: merchantIdentity.email,
    billing_email: merchantIdentity.billing_email,
    invoice_frequency: invoiceFrequency || 'single',
    store_id: storeId || '',
    store_name: storeName || ''
  };
};

const buildUsersProxyMerchantPaymentProfilePayload = ({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail, invoiceFrequency, storeId, storeName, isAdminView }) => ({
  ...buildDirectMerchantPaymentProfilePayload({
    primaryOwner,
    merchantAuth0Id,
    associatedUsers,
    billingEmail,
    invoiceFrequency,
    storeId,
    storeName
  }),
  merchant_auth0_id: merchantAuth0Id,
  merchant_auth0_ids: getMerchantAuth0Ids({ primaryOwner, associatedUsers, isAdminView })
});

const resolveDirectMerchantPaymentProfilePayload = async ({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail, invoiceFrequency, storeId, storeName }) => {
  let directPayload = buildDirectMerchantPaymentProfilePayload({
    primaryOwner,
    merchantAuth0Id,
    associatedUsers,
    billingEmail,
    invoiceFrequency,
    storeId,
    storeName
  });

  if (directPayload.email) {
    return directPayload;
  }

  try {
    const existingProfile = await fetchDirectMerchantPaymentProfile(merchantAuth0Id);
    directPayload = mergeMerchantIdentityIntoPayload(directPayload, existingProfile);
  } catch (error) {
    if (!isDirectProfileRecoverableError(error)) {
      throw error;
    }
  }

  return directPayload;
};

const fetchMerchantPaymentProfile = async ({ merchantAuth0Id, merchantAuth0Ids, isAdminView, storeId }) => {
  if (storeId) {
    const storeProfile = await fetchStorePaymentProfile(storeId, {
      allowAdminFallbackOn404: true,
      admin: isAdminView,
      merchantAuth0Id
    });

    const resolvedStoreProfile = getPaymentProfilePayload(storeProfile);

    if (resolvedStoreProfile) {
      return resolvedStoreProfile;
    }
  }

  const candidateAuth0Ids = Array.isArray(merchantAuth0Ids) && merchantAuth0Ids.length > 0 ? merchantAuth0Ids : [merchantAuth0Id];

  try {
    const selfServicePaymentProfile = await fetchSelfPaymentProfile({
      auth0Id: merchantAuth0Id || getCurrentAuth0Id(),
      auth0Ids: candidateAuth0Ids,
      preferUsersProxy: true,
      provisionMerchantProfile: false,
      currentUserProfileFallback: !isAdminView
    });

    return getPaymentProfilePayload(selfServicePaymentProfile);
  } catch (error) {
    if (isDirectProfileRecoverableError(error) || shouldFallbackToUsersProxy(error)) {
      return null;
    }

    throw error;
  }
};

const updateOrCreateDirectProfile = async (auth0Id, payload) => {
  try {
    const response = await axios({
      method: 'PUT',
      url: getDirectMerchantPaymentProfileUrl(auth0Id),
      headers: getRequestHeaders(),
      data: {
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: payload.email,
        billing_email: payload.billing_email,
        invoice_frequency: payload.invoice_frequency
      }
    });

    return getResponsePayload(response);
  } catch (error) {
    if (!isDirectProfileCreateableError(error)) {
      throw error;
    }

    await axios({
      method: 'POST',
      url: getDirectMerchantPaymentProfilesUrl(),
      headers: getRequestHeaders(),
      data: { ...payload, user_auth0_id: auth0Id }
    });

    const response = await axios({
      method: 'PUT',
      url: getDirectMerchantPaymentProfileUrl(auth0Id),
      headers: getRequestHeaders(),
      data: {
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: payload.email,
        billing_email: payload.billing_email,
        invoice_frequency: payload.invoice_frequency
      }
    });

    return getResponsePayload(response);
  }
};

const saveDirectMerchantPaymentProfile = async ({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail, invoiceFrequency, storeId, storeName }) => {
  const directPayload = await resolveDirectMerchantPaymentProfilePayload({
    primaryOwner,
    merchantAuth0Id,
    associatedUsers,
    billingEmail,
    invoiceFrequency,
    storeId,
    storeName
  });

  if (!directPayload.email) {
    throw new Error('Merchant billing profile requires an email address before payment terms can be saved.');
  }

  const primaryResult = await updateOrCreateDirectProfile(merchantAuth0Id, directPayload);

  const allMerchantAuth0Ids = getMerchantAuth0Ids({ primaryOwner, associatedUsers, isAdminView: true });
  const secondaryAuth0Ids = [...new Set(allMerchantAuth0Ids.filter(id => id && id !== merchantAuth0Id))];

  for (const auth0Id of secondaryAuth0Ids) {
    try {
      const user = Array.isArray(associatedUsers)
        ? associatedUsers.find(u => u && u.auth0_id === auth0Id)
        : null;

      const secondaryPayload = {
        first_name: (user && user.firstname) || directPayload.first_name,
        last_name: (user && user.lastname) || directPayload.last_name,
        email: (user && user.email) || directPayload.email,
        billing_email: billingEmail || directPayload.billing_email,
        invoice_frequency: invoiceFrequency || directPayload.invoice_frequency,
        store_id: storeId || '',
        store_name: storeName || ''
      };

      await updateOrCreateDirectProfile(auth0Id, secondaryPayload);
    } catch (secondaryError) {
      // Best-effort for secondary users — do not fail the entire save
    }
  }

  return primaryResult;
};

const saveMerchantPaymentProfile = async ({ primaryOwner, merchantAuth0Id, associatedUsers, billingEmail, invoiceFrequency, storeId, storeName, isAdminView }) => {
  const proxyPayload = buildUsersProxyMerchantPaymentProfilePayload({
    primaryOwner,
    merchantAuth0Id,
    associatedUsers,
    billingEmail,
    invoiceFrequency,
    storeId,
    storeName,
    isAdminView
  });

  console.log('[BillingSave] starting save', { storeId, merchantAuth0Id, invoiceFrequency, email: proxyPayload && proxyPayload.email, billingEmail: proxyPayload && proxyPayload.billing_email, isAdminView });

  if (storeId) {
    try {
      const response = isAdminView
        ? await putViaCandidateUrls({
            urls: getWritableStorePaymentProfileUrls(storeId),
            data: proxyPayload,
            stopOnHttpResponse: isLocalDashboardEnvironment()
          })
        : await axios({
            method: 'PUT',
            url: merchant_urls.storePaymentProfile(storeId),
            headers: getRequestHeaders(),
            data: {
              billing_email: billingEmail,
              invoice_frequency: invoiceFrequency
            }
          });

      const result = getResponsePayload(response);
      console.log('[BillingSave] store-payment-profile succeeded', { storeId, merchantAuth0Id, invoiceFrequency, responseFrequency: result && result.invoice_frequency });
      return result;
    } catch (error) {
      console.warn('[BillingSave] store-payment-profile failed, checking fallback', { storeId, status: error && error.response && error.response.status, message: error && error.message });
      if (!shouldFallbackFromStorePaymentProfile(error)) {
        throw error;
      }
    }
  }

  if (isAdminView) {
    try {
      const response = await putViaCandidateUrls({
        urls: getAdminMerchantPaymentProfileUrls(merchantAuth0Id),
        data: proxyPayload,
        stopOnHttpResponse: isLocalDashboardEnvironment()
      });

      const result = getResponsePayload(response);
      console.log('[BillingSave] merchant-payment-profile succeeded', { merchantAuth0Id, invoiceFrequency, responseFrequency: result && result.invoice_frequency });
      return result;
    } catch (error) {
      console.warn('[BillingSave] merchant-payment-profile failed, checking fallback', { merchantAuth0Id, status: error && error.response && error.response.status, message: error && error.message });
      if (!shouldFallbackToUsersProxy(error)) {
        throw error;
      }

      try {
        return await saveDirectMerchantPaymentProfile({
          primaryOwner,
          merchantAuth0Id,
          associatedUsers,
          billingEmail,
          invoiceFrequency,
          storeId,
          storeName
        });
      } catch (directSaveError) {
        if (isLocalDashboardEnvironment() && isLikelyCorsOrNetworkError(directSaveError)) {
          throw new Error('Billing save failed through api-users and direct payments fallback was blocked by a local browser CORS/network restriction.');
        }

        throw directSaveError;
      }
    }
  }

  try {
    return await saveDirectMerchantPaymentProfile({
      primaryOwner,
      merchantAuth0Id,
      associatedUsers,
      billingEmail,
      invoiceFrequency,
      storeId,
      storeName
    });
  } catch (error) {
    if (!shouldFallbackToUsersProxy(error)) {
      throw error;
    }

    const response = await axios({
      method: 'PUT',
      url: user_urls.merchantPaymentProfile(merchantAuth0Id),
      headers: getRequestHeaders(),
      data: {
        billing_email: billingEmail,
        invoice_frequency: invoiceFrequency
      }
    });

    return getResponsePayload(response);
  }
};

export default function MerchantList(props) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [currentUserStoreId, setCurrentUserStoreId] = useState('');
  const [currentUserStoreLoading, setCurrentUserStoreLoading] = useState(false);
  const [customerType, setCustomerType] = useState('businesses');
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedStoreDetail, setSelectedStoreDetail] = useState(null);
  const [associatedUsers, setAssociatedUsers] = useState([]);
  const [associatedUserActivity, setAssociatedUserActivity] = useState({});
  const [passwordResetStatusById, setPasswordResetStatusById] = useState({});

  // Fetch Auth0 last-login for each associated user (small list; a few calls on expand).
  const associatedUserIdsKey = (associatedUsers || []).map(candidate => candidate && candidate.auth0_id).filter(Boolean).join(',');
  useEffect(() => {
    const ids = associatedUserIdsKey ? associatedUserIdsKey.split(',') : [];
    if (ids.length === 0) {
      return undefined;
    }
    let cancelled = false;
    const token = localStorage.getItem('access_token');
    ids.forEach(id => {
      axios({ method: 'get', url: user_urls.userAuth0Activity(id), timeout: 10000, headers: { Authorization: 'Bearer ' + token } })
        .then(res => {
          if (cancelled) return;
          const activityPayload = res && res.data && res.data.payload ? res.data.payload : res && res.data;
          setAssociatedUserActivity(previous => ({ ...previous, [id]: activityPayload || null }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [associatedUserIdsKey]);

  const sendPasswordResetForUser = userRecord => {
    const email = userRecord && userRecord.email ? String(userRecord.email).trim() : '';
    const statusKey = (userRecord && userRecord.auth0_id) || email;
    if (!email) {
      setPasswordResetStatusById(previous => ({ ...previous, [statusKey]: 'No email on file' }));
      return;
    }
    setPasswordResetStatusById(previous => ({ ...previous, [statusKey]: 'Sending…' }));
    axios({
      method: 'POST',
      url: 'https://' + config.config.AUTH_CONFIG.domain + '/dbconnections/change_password',
      headers: { 'content-type': 'application/json' },
      data: { client_id: config.config.AUTH_CONFIG.clientId, email, connection: 'Username-Password-Authentication' }
    })
      .then(() => setPasswordResetStatusById(previous => ({ ...previous, [statusKey]: 'Reset email sent' })))
      .catch(() => setPasswordResetStatusById(previous => ({ ...previous, [statusKey]: 'Failed to send' })));
  };
  const [, setAssociatedUsersDiagnostics] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [originalHours, setOriginalHours] = useState(createEmptyHours());
  const [draftHours, setDraftHours] = useState(createEmptyHours());
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursNotice, setHoursNotice] = useState('');
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [storeEditMode, setStoreEditMode] = useState(false);
  const [storeSaving, setStoreSaving] = useState(false);
  const [storeNotice, setStoreNotice] = useState('');
  const [storeForm, setStoreForm] = useState(buildStoreForm(null));
  const [paymentProfile, setPaymentProfile] = useState(() => buildPaymentProfileState());
  const [savedPaymentProfile, setSavedPaymentProfile] = useState(() => buildPaymentProfileState());
  const [paymentProfileLoading, setPaymentProfileLoading] = useState(false);
  const [paymentProfileError, setPaymentProfileError] = useState('');
  const [usersEditMode, setUsersEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [userSavingId, setUserSavingId] = useState('');
  const [userNotice, setUserNotice] = useState('');
  const [userForm, setUserForm] = useState(buildUserForm(null));
  const [showCreateMerchantModal, setShowCreateMerchantModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const isStoreEditModeRef = useRef(false);
  const billingSavedAtRef = useRef(0);
  const activeStoreDetailsRequestRef = useRef('');
  const forcedStoreAccessHydrationRef = useRef(new Set());
  const { marketIdMerchants } = useGlobalContext();
  const isAdminView = role !== null && role < 200;
  const assignableUserRoleOptions = useMemo(() => getAssignableUserRoleOptions(role), [role]);

  useEffect(() => {
    isStoreEditModeRef.current = storeEditMode;
  }, [storeEditMode]);

  useEffect(() => {
    if (isAdminView || !selectedStoreId) {
      return;
    }

    const currentAuth0Id = getCurrentAuth0Id();

    if (!currentAuth0Id) {
      return;
    }

    let active = true;

    fetchMerchantStoreAccessPayloadAcrossHosts(currentAuth0Id)
      .then(payload => {
        if (!active || !payload) {
          return;
        }

        const linkedUsersForStore = resolveStoreAccessLinkedUsersForStore({ payload, storeId: selectedStoreId });

        if (linkedUsersForStore.length === 0) {
          return;
        }

        setAssociatedUsers(previousUsers => {
          const previousUsersList = Array.isArray(previousUsers) ? previousUsers : [];
          const immediateUsers = dedupeAssociatedUsers([...previousUsersList, ...linkedUsersForStore]);

          return immediateUsers.length > previousUsersList.length ? immediateUsers : previousUsers;
        });

        setAssociatedUsersDiagnostics(previousDiagnostics => ({
          usersHasuraCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraCount)
            ? previousDiagnostics.usersHasuraCount
            : 0,
          usersHasuraExpandedCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraExpandedCount)
            ? previousDiagnostics.usersHasuraExpandedCount
            : 0,
          ownershipFallbackCount: previousDiagnostics && Number.isFinite(previousDiagnostics.ownershipFallbackCount)
            ? previousDiagnostics.ownershipFallbackCount
            : 0,
          paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount)
            ? previousDiagnostics.paymentProfileMerchantCount
            : 0,
          adminUsersStoreCount: linkedUsersForStore.length,
          storeAccessCandidatesCount: previousDiagnostics && Number.isFinite(previousDiagnostics.storeAccessCandidatesCount)
            ? previousDiagnostics.storeAccessCandidatesCount
            : 1,
          finalCount: previousDiagnostics && Number.isFinite(previousDiagnostics.finalCount)
            ? Math.max(previousDiagnostics.finalCount, linkedUsersForStore.length)
            : linkedUsersForStore.length,
          adminUsersSourceHost: 'store-access-current-immediate',
          storePaymentProfileSourceHost: previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost
            ? previousDiagnostics.storePaymentProfileSourceHost
            : 'store-access-current-immediate',
        }));
      })
      .catch(() => null);

    return () => {
      active = false;
    };
  }, [isAdminView, selectedStoreId]);

  useEffect(() => {
    if (!selectedStoreId || !Array.isArray(associatedUsers) || associatedUsers.length > 1) {
      return;
    }

    const seedUser = associatedUsers[0];
    const currentAuth0Id = !isAdminView ? getCurrentAuth0Id() : '';
    const seedAuth0Id = currentAuth0Id || (seedUser && seedUser.auth0_id ? String(seedUser.auth0_id) : '');

    if (!seedAuth0Id) {
      return;
    }

    const hydrationKey = `${String(selectedStoreId)}::${seedAuth0Id}`;

    if (forcedStoreAccessHydrationRef.current.has(hydrationKey)) {
      return;
    }

    forcedStoreAccessHydrationRef.current.add(hydrationKey);

    (async () => {
      const payload = await fetchMerchantStoreAccessPayloadAcrossHosts(seedAuth0Id).catch(() => null);

      if (!payload || !Array.isArray(payload.linked_merchants) || payload.linked_merchants.length === 0) {
        return;
      }

      const hydratedUsers = dedupeAssociatedUsers(mapLinkedMerchantsToAssociatedUsers(payload.linked_merchants));

      if (hydratedUsers.length <= associatedUsers.length) {
        return;
      }

      setAssociatedUsers(hydratedUsers);
      setAssociatedUsersDiagnostics(previousDiagnostics => ({
        usersHasuraCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraCount) ? previousDiagnostics.usersHasuraCount : 0,
        usersHasuraExpandedCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraExpandedCount) ? previousDiagnostics.usersHasuraExpandedCount : 0,
        ownershipFallbackCount: previousDiagnostics && Number.isFinite(previousDiagnostics.ownershipFallbackCount) ? previousDiagnostics.ownershipFallbackCount : 1,
        paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount) ? previousDiagnostics.paymentProfileMerchantCount : 0,
        adminUsersStoreCount: hydratedUsers.length,
        storeAccessCandidatesCount: 1,
        finalCount: hydratedUsers.length,
        adminUsersSourceHost: 'store-access-forced-hydrate',
        storePaymentProfileSourceHost: previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost
          ? previousDiagnostics.storePaymentProfileSourceHost
          : 'store-access-forced-hydrate',
      }));
    })();
  }, [selectedStoreId, associatedUsers, isAdminView]);

  function getStores(currentRole) {
    const isAdminView = Array.isArray(currentRole) ? currentRole[0] < 200 : currentRole < 200;
    if (!isAdminView && !currentUserStoreId) {
      setLoading(false);
      setStores([]);
      return;
    }

    const storeWhereClause = isAdminView ? '' : `where: {store_id: {_eq: "${currentUserStoreId}"}},`;

    postInventoryHasuraQuery(
      `{Stores(${storeWhereClause}order_by: [{is_enabled: desc}, {is_individual_user: asc}, {name: asc}]) {
        store_id
        city
        state
        name
        phone
        logo
        address_1
        address_2
        is_enabled
        is_individual_user
        StoreOwnerships {
          firstname
          lastname
          email
          merchant_id
        }
      }}`
    )
      .then(response => {
        setLoading(false);
        if (response.data.errors) {
          if (response.data.errors.length > 0) {
            const firstError = response.data.errors[0] || {};
            const errorMessage = firstError.message;
            const errorCode = firstError.extensions && firstError.extensions.code;

            if (errorMessage === 'Malformed Authorization header' || errorCode === 'invalid-jwt') {
              setDetailError(previousError => previousError || 'Session refresh in progress. Retrying data sync...');
              return;
            }
          }
        }
        if (response.data.data) {
          setStores(response.data.data.Stores);
        }
      })
      .catch(error => {
        setLoading(false);
        setDetailError(getRequestErrorMessage(error, 'Unable to load customers.'));
      });
  }

  function getStoreDetails(storeId) {
    const requestToken = `${storeId}:${Date.now()}`;
    const detailsStartedAt = Date.now();
    activeStoreDetailsRequestRef.current = requestToken;
    const isActiveRequest = () => activeStoreDetailsRequestRef.current === requestToken;
    const isBillingStale = () => billingSavedAtRef.current > detailsStartedAt;

    const warmStore = Array.isArray(stores)
      ? stores.find(candidateStore => candidateStore && String(candidateStore.store_id || '') === String(storeId))
      : null;
    const shouldShowBlockingDetailLoading = !warmStore;

    if (warmStore) {
      const warmOwnershipFallbackUsers = Array.isArray(warmStore.StoreOwnerships)
        ? warmStore.StoreOwnerships
            .filter(owner => owner && (owner.email || owner.merchant_id))
            .map(owner => ({
              auth0_id: owner.merchant_id || owner.email || '',
              username: owner.email || '',
              firstname: owner.firstname || '',
              lastname: owner.lastname || '',
              email: owner.email || '',
              phone: '',
              Roles: [{ role_id: '', role_description: 'store_owner', role: 220 }]
            }))
        : [];

      const warmStoreDetail = applyStoredStoreDeliveryTypeOverride({
        ...warmStore,
        StoreTimes: []
      });
      const warmFallbackAccountingEmail = getFallbackAccountingEmail(warmStoreDetail) || getFallbackAccountingEmailFromUsers(warmOwnershipFallbackUsers);
      const warmPaymentProfile = buildPaymentProfileState({
        billingEmail: warmFallbackAccountingEmail,
        invoiceFrequency: getStoredStoreInvoiceFrequency(storeId)
      });

      setSelectedStoreDetail(warmStoreDetail);
      setAssociatedUsers(sortUsersByName(warmOwnershipFallbackUsers));
      setOriginalHours(createEmptyHours());
      setDraftHours(createEmptyHours());
      setStoreForm(buildStoreForm(warmStoreDetail));
      if (!isBillingStale()) {
        setPaymentProfile(warmPaymentProfile);
        setSavedPaymentProfile(warmPaymentProfile);
      }
      setAssociatedUsersDiagnostics({
        usersHasuraCount: 0,
        usersHasuraExpandedCount: 0,
        ownershipFallbackCount: warmOwnershipFallbackUsers.length,
        paymentProfileMerchantCount: 0,
        adminUsersStoreCount: 0,
        storeAccessCandidatesCount: warmOwnershipFallbackUsers.length > 0 ? 1 : 0,
        finalCount: warmOwnershipFallbackUsers.length,
        adminUsersSourceHost: 'warm-start',
        storePaymentProfileSourceHost: 'warm-start',
      });
    }

    setDetailLoading(shouldShowBlockingDetailLoading);
    setDetailError('');
    setHoursNotice('');
    setStoreNotice('');
    setUserNotice('');
    setPaymentProfileError('');
    if (shouldShowBlockingDetailLoading) {
      setAssociatedUsersDiagnostics(null);
    }

    const loadingGuardId = window.setTimeout(() => {
      if (!isActiveRequest()) {
        return;
      }

      setDetailLoading(false);
      if (shouldShowBlockingDetailLoading) {
        setDetailError(previousError => previousError || 'Store details request timed out. Please retry.');
      }
    }, MERCHANT_LIST_REQUEST_TIMEOUT_MS + 3000);

    const storeRequest = postInventoryHasuraQuery(buildStoreBaseDetailQuery(storeId));

    const storeTimesRequest = postInventoryHasuraQuery(buildStoreTimesQuery(storeId));

    const storePaymentProfileRequest = fetchStorePaymentProfileAcrossHosts(storeId);

    const adminUsersByStoreRequest = fetchAdminUsersByStoreAcrossHosts(storeId);

    const usersQuery = `{Users(where: {store_id: {_eq: "${storeId}"}}, order_by: [{firstname: asc}, {lastname: asc}]) {
      user_id
      auth0_id
      username
      firstname
      lastname
      email
      phone
      access_users
      Roles {
        role_id
        role_description
        role
      }
    }}`;

    const requestUsersHasuraQuery = async query => {
      const requestWithAdminSecret = () => axios({
        method: 'post',
        url: user_urls.hasuraHost,
        timeout: MERCHANT_LIST_REQUEST_TIMEOUT_MS,
        headers: {
          'content-type': 'application/json',
          'x-hasura-admin-secret': config.config.api_users.admin.usersHasuraAdminSecret
        },
        data: {
          query
        }
      });

      const requestWithBearer = () => axios({
        method: 'post',
        url: user_urls.hasuraHost,
        timeout: MERCHANT_LIST_REQUEST_TIMEOUT_MS,
        headers: {
          Authorization: 'Bearer ' + getPreferredAuthToken(),
          'content-type': 'application/json'
        },
        data: {
          query
        }
      });

      try {
        const adminSecretResponse = await requestWithAdminSecret();

        if (
          isDashboardLocalDebugMode()
          && adminSecretResponse
          && adminSecretResponse.data
          && Array.isArray(adminSecretResponse.data.errors)
          && adminSecretResponse.data.errors.length > 0
        ) {
          return requestWithBearer();
        }

        return adminSecretResponse;
      } catch (adminSecretError) {
        if (!isDashboardLocalDebugMode()) {
          throw adminSecretError;
        }

        return requestWithBearer();
      }
    };

    const usersRequest = requestUsersHasuraQuery(usersQuery);

    Promise.all([storeRequest, storeTimesRequest])
      .then(([storeResponse, storeTimesResponse]) => {
        if (!isActiveRequest()) {
          return null;
        }

        const storeErrorMessage = getGraphqlErrorMessage(storeResponse);
        if (storeErrorMessage) {
          throw new Error(storeErrorMessage);
        }

        const storeResponseRecord =
          storeResponse && storeResponse.data && storeResponse.data.data && storeResponse.data.data.Stores && storeResponse.data.data.Stores.length > 0
            ? storeResponse.data.data.Stores[0]
            : null;
        const storeTimesErrorMessage = getGraphqlErrorMessage(storeTimesResponse);
        const storeTimesRecord = !storeTimesErrorMessage
          && storeTimesResponse && storeTimesResponse.data && storeTimesResponse.data.data && Array.isArray(storeTimesResponse.data.data.Stores)
          ? storeTimesResponse.data.data.Stores[0]
          : null;

        const store = applyStoredStoreDeliveryTypeOverride({
          ...(storeResponseRecord || {}),
          StoreTimes: storeTimesRecord && Array.isArray(storeTimesRecord.StoreTimes) ? storeTimesRecord.StoreTimes : []
        });

        if (!store) {
          setSelectedStoreDetail(null);
          setAssociatedUsers([]);
          setOriginalHours(createEmptyHours());
          setDraftHours(createEmptyHours());
          setDetailError('Store details were not found.');
          return null;
        }

        const ownershipFallbackUsers = Array.isArray(store.StoreOwnerships)
          ? store.StoreOwnerships
              .filter(owner => owner && (owner.email || owner.merchant_id))
              .map(owner => ({
                auth0_id: owner.merchant_id || owner.email || '',
                username: owner.email || '',
                firstname: owner.firstname || '',
                lastname: owner.lastname || '',
                email: owner.email || '',
                phone: '',
                Roles: [{ role_id: '', role_description: 'store_owner', role: 220 }]
              }))
          : [];
        const normalizedHours = normalizeStoreTimes(store.StoreTimes);
        const fallbackAccountingEmail = getFallbackAccountingEmail(store) || getFallbackAccountingEmailFromUsers(ownershipFallbackUsers);
        const nextPaymentProfile = buildPaymentProfileState({
          billingEmail: fallbackAccountingEmail,
          invoiceFrequency: getStoredStoreInvoiceFrequency(storeId)
        });

        setSelectedStoreDetail(store);
        setAssociatedUsers(sortUsersByName(ownershipFallbackUsers));
        setAssociatedUsersDiagnostics({
          usersHasuraCount: 0,
          usersHasuraExpandedCount: 0,
          ownershipFallbackCount: ownershipFallbackUsers.length,
          paymentProfileMerchantCount: 0,
          adminUsersStoreCount: 0,
          storeAccessCandidatesCount: 0,
          finalCount: ownershipFallbackUsers.length,
          adminUsersSourceHost: '',
          storePaymentProfileSourceHost: '',
        });
        setOriginalHours(normalizedHours);
        setDraftHours(normalizedHours);
        setStoreForm(buildStoreForm(store));
        if (!isBillingStale()) {
          setPaymentProfile(nextPaymentProfile);
          setSavedPaymentProfile(nextPaymentProfile);
        }

        if (!isAdminView) {
          const currentMerchantAuth0Id = getCurrentAuth0Id();

          if (currentMerchantAuth0Id) {
            fetchMerchantStoreAccessPayloadAcrossHosts(currentMerchantAuth0Id)
              .then(storeAccessPayload => {
                if (!isActiveRequest() || !storeAccessPayload) {
                  return;
                }

                const linkedUsers = mapLinkedMerchantsToAssociatedUsers(storeAccessPayload.linked_merchants);
                const immediateUsers = dedupeAssociatedUsers([...(ownershipFallbackUsers || []), ...linkedUsers]);

                if (immediateUsers.length <= (ownershipFallbackUsers || []).length) {
                  return;
                }

                setAssociatedUsers(previousUsers => (immediateUsers.length > previousUsers.length ? immediateUsers : previousUsers));
                setAssociatedUsersDiagnostics(previousDiagnostics => ({
                  usersHasuraCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraCount)
                    ? previousDiagnostics.usersHasuraCount
                    : 0,
                  usersHasuraExpandedCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraExpandedCount)
                    ? previousDiagnostics.usersHasuraExpandedCount
                    : 0,
                  ownershipFallbackCount: ownershipFallbackUsers.length,
                  paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount)
                    ? previousDiagnostics.paymentProfileMerchantCount
                    : 0,
                  adminUsersStoreCount: linkedUsers.length,
                  storeAccessCandidatesCount: previousDiagnostics && Number.isFinite(previousDiagnostics.storeAccessCandidatesCount)
                    ? previousDiagnostics.storeAccessCandidatesCount
                    : 1,
                  finalCount: immediateUsers.length,
                  adminUsersSourceHost: 'store-access-current-fast',
                  storePaymentProfileSourceHost: previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost
                    ? previousDiagnostics.storePaymentProfileSourceHost
                    : 'store-access-current-fast',
                }));
              })
              .catch(() => null);
          }
        }

        return {
          store,
          ownershipFallbackUsers,
        };
      })
      .then(async coreResult => {
        if (!coreResult || !isActiveRequest()) {
          return;
        }

        if (!isAdminView) {
          const directUsersByStoreResponse = await fetchUsersByStoreAcrossHasuraHosts(storeId).catch(() => ({
            users: [],
            sourceHost: '',
            authMode: '',
          }));

          if (!isActiveRequest()) {
            return;
          }

          const usersByStore = directUsersByStoreResponse && Array.isArray(directUsersByStoreResponse.users)
            ? dedupeAssociatedUsers(directUsersByStoreResponse.users)
            : [];

          if (usersByStore.length > 0) {
            setAssociatedUsers(usersByStore);
            setAssociatedUsersDiagnostics(previousDiagnostics => ({
              usersHasuraCount: usersByStore.length,
              usersHasuraExpandedCount: 0,
              ownershipFallbackCount: (coreResult.ownershipFallbackUsers || []).length,
              paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount)
                ? previousDiagnostics.paymentProfileMerchantCount
                : 0,
              adminUsersStoreCount: 0,
              storeAccessCandidatesCount: 0,
              finalCount: usersByStore.length,
              adminUsersSourceHost: 'store-id-authoritative',
              storePaymentProfileSourceHost: (directUsersByStoreResponse && directUsersByStoreResponse.sourceHost)
                || (previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost)
                || '',
            }));
          } else {
            const ownershipUsers = dedupeAssociatedUsers(coreResult.ownershipFallbackUsers || []);
            setAssociatedUsers(ownershipUsers);
            setAssociatedUsersDiagnostics(previousDiagnostics => ({
              usersHasuraCount: 0,
              usersHasuraExpandedCount: 0,
              ownershipFallbackCount: ownershipUsers.length,
              paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount)
                ? previousDiagnostics.paymentProfileMerchantCount
                : 0,
              adminUsersStoreCount: 0,
              storeAccessCandidatesCount: 0,
              finalCount: ownershipUsers.length,
              adminUsersSourceHost: 'store-id-authoritative-empty',
              storePaymentProfileSourceHost: (directUsersByStoreResponse && directUsersByStoreResponse.sourceHost)
                || (previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost)
                || '',
            }));
          }

          return;
        }

        const ownershipAuth0Candidates = (coreResult.ownershipFallbackUsers || [])
          .map(candidateUser => (candidateUser && candidateUser.auth0_id ? String(candidateUser.auth0_id) : ''))
          .filter(Boolean);
        const preliminaryStoreAccessCandidates = [...new Set([
          ...ownershipAuth0Candidates,
          ...(!isAdminView ? [getCurrentAuth0Id()] : []),
        ].filter(Boolean))];

        if (preliminaryStoreAccessCandidates.length > 0) {
          try {
            const preliminaryStoreAccessResponses = await Promise.allSettled(
              preliminaryStoreAccessCandidates.map(candidateAuth0Id =>
                fetchMerchantStoreAccessPayloadAcrossHosts(candidateAuth0Id)
              )
            );

            const preliminaryStoreAccessPayloads = preliminaryStoreAccessResponses
              .filter(candidateResponse => candidateResponse.status === 'fulfilled' && candidateResponse.value)
              .map(candidateResponse => candidateResponse.value);

            const preliminaryStoreAccessUsers = preliminaryStoreAccessPayloads
              .flatMap(candidatePayload => Array.isArray(candidatePayload.linked_merchants) ? candidatePayload.linked_merchants : [])
              .filter(Boolean)
              .map(candidateUser => ({
                auth0_id: candidateUser.auth0_id || '',
                user_id: candidateUser.user_id || '',
                username: candidateUser.username || candidateUser.email || '',
                firstname: candidateUser.firstname || '',
                lastname: candidateUser.lastname || '',
                email: candidateUser.email || '',
                phone: candidateUser.phone || '',
                access_users: Array.isArray(candidateUser.access_users) ? candidateUser.access_users : [],
                Roles: Array.isArray(candidateUser.roles)
                  ? candidateUser.roles.map(roleRecord => ({
                      role_id: '',
                      role_description: roleRecord && roleRecord.role_description ? roleRecord.role_description : '',
                      role: roleRecord && Number.isInteger(roleRecord.role) ? roleRecord.role : 220,
                    }))
                  : [{ role_id: '', role_description: 'store_user', role: 220 }]
              }));

            const preliminaryUsers = sortUsersByName(
              [...(coreResult.ownershipFallbackUsers || []), ...preliminaryStoreAccessUsers]
                .filter(Boolean)
                .reduce((dedupedUsers, candidateUser) => {
                  const dedupeKey =
                    (candidateUser && candidateUser.auth0_id && String(candidateUser.auth0_id))
                    || (candidateUser && candidateUser.email && String(candidateUser.email).toLowerCase())
                    || null;

                  if (!dedupeKey) {
                    dedupedUsers.push(candidateUser);
                    return dedupedUsers;
                  }

                  if (!dedupedUsers.some(existingUser => {
                    const existingKey =
                      (existingUser && existingUser.auth0_id && String(existingUser.auth0_id))
                      || (existingUser && existingUser.email && String(existingUser.email).toLowerCase())
                      || null;

                    return existingKey === dedupeKey;
                  })) {
                    dedupedUsers.push(candidateUser);
                  }

                  return dedupedUsers;
                }, [])
            );

            if (preliminaryUsers.length > (coreResult.ownershipFallbackUsers || []).length) {
              setAssociatedUsers(preliminaryUsers);
              setAssociatedUsersDiagnostics({
                usersHasuraCount: 0,
                usersHasuraExpandedCount: 0,
                ownershipFallbackCount: (coreResult.ownershipFallbackUsers || []).length,
                paymentProfileMerchantCount: 0,
                adminUsersStoreCount: preliminaryStoreAccessUsers.length,
                storeAccessCandidatesCount: preliminaryStoreAccessCandidates.length,
                finalCount: preliminaryUsers.length,
                adminUsersSourceHost: 'store-access-preload',
                storePaymentProfileSourceHost: 'store-access-preload',
              });
            }
          } catch (error) {
            void error;
          }
        }

        usersRequest
          .then(usersResponse => {
            if (!isActiveRequest()) {
              return;
            }

            const usersByStore = extractUsersFromHasuraResponse(usersResponse);
            const immediateUsers = dedupeAssociatedUsers([
              ...(coreResult.ownershipFallbackUsers || []),
              ...usersByStore,
            ]);

            if (immediateUsers.length <= (coreResult.ownershipFallbackUsers || []).length) {
              return;
            }

            setAssociatedUsers(previousUsers => (immediateUsers.length > previousUsers.length ? immediateUsers : previousUsers));
            setAssociatedUsersDiagnostics(previousDiagnostics => ({
              usersHasuraCount: usersByStore.length,
              usersHasuraExpandedCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraExpandedCount)
                ? previousDiagnostics.usersHasuraExpandedCount
                : 0,
              ownershipFallbackCount: (coreResult.ownershipFallbackUsers || []).length,
              paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount)
                ? previousDiagnostics.paymentProfileMerchantCount
                : 0,
              adminUsersStoreCount: previousDiagnostics && Number.isFinite(previousDiagnostics.adminUsersStoreCount)
                ? previousDiagnostics.adminUsersStoreCount
                : 0,
              storeAccessCandidatesCount: previousDiagnostics && Number.isFinite(previousDiagnostics.storeAccessCandidatesCount)
                ? previousDiagnostics.storeAccessCandidatesCount
                : 0,
              finalCount: immediateUsers.length,
              adminUsersSourceHost: previousDiagnostics && previousDiagnostics.adminUsersSourceHost
                ? previousDiagnostics.adminUsersSourceHost
                : 'users-hasura-early',
              storePaymentProfileSourceHost: previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost
                ? previousDiagnostics.storePaymentProfileSourceHost
                : 'users-hasura-early',
            }));
          })
          .catch(() => null);

        adminUsersByStoreRequest
          .then(adminUsersByStoreValue => {
            if (!isActiveRequest() || !adminUsersByStoreValue) {
              return;
            }

            const adminLinkedUsers = resolveAssociatedUsersFromAdminDataset({
              adminUsers: Array.isArray(adminUsersByStoreValue.users) ? adminUsersByStoreValue.users : [],
              storeId,
              ownershipMerchantAuth0Ids: [],
              paymentProfileMerchantAuth0Ids: [],
              hasuraUsers: []
            });

            const earlyUsers = dedupeAssociatedUsers([...(coreResult.ownershipFallbackUsers || []), ...adminLinkedUsers]);

            if (earlyUsers.length <= (coreResult.ownershipFallbackUsers || []).length) {
              return;
            }

            setAssociatedUsers(previousUsers => (earlyUsers.length > previousUsers.length ? earlyUsers : previousUsers));
            setAssociatedUsersDiagnostics(previousDiagnostics => ({
              usersHasuraCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraCount) ? previousDiagnostics.usersHasuraCount : 0,
              usersHasuraExpandedCount: previousDiagnostics && Number.isFinite(previousDiagnostics.usersHasuraExpandedCount) ? previousDiagnostics.usersHasuraExpandedCount : 0,
              ownershipFallbackCount: (coreResult.ownershipFallbackUsers || []).length,
              paymentProfileMerchantCount: previousDiagnostics && Number.isFinite(previousDiagnostics.paymentProfileMerchantCount) ? previousDiagnostics.paymentProfileMerchantCount : 0,
              adminUsersStoreCount: adminLinkedUsers.length,
              storeAccessCandidatesCount: previousDiagnostics && Number.isFinite(previousDiagnostics.storeAccessCandidatesCount)
                ? previousDiagnostics.storeAccessCandidatesCount
                : 0,
              finalCount: earlyUsers.length,
              adminUsersSourceHost: adminUsersByStoreValue.sourceHost || 'admin-users-early',
              storePaymentProfileSourceHost: previousDiagnostics && previousDiagnostics.storePaymentProfileSourceHost
                ? previousDiagnostics.storePaymentProfileSourceHost
                : '',
            }));
          })
          .catch(() => null);

        Promise.allSettled([usersRequest, storePaymentProfileRequest, adminUsersByStoreRequest])
          .then(async results => {
            if (!isActiveRequest()) {
              return;
            }

            const [usersResult, storePaymentProfileResult, adminUsersByStoreResult] = results;
            const usersResponse = usersResult.status === 'fulfilled' ? usersResult.value : null;
            const usersByStore = extractUsersFromHasuraResponse(usersResponse);
            const storePaymentProfilePayload =
              storePaymentProfileResult && storePaymentProfileResult.status === 'fulfilled' && storePaymentProfileResult.value
                ? storePaymentProfileResult.value.payload
                : null;
            const storePaymentProfileSourceHost =
              storePaymentProfileResult && storePaymentProfileResult.status === 'fulfilled' && storePaymentProfileResult.value
                ? storePaymentProfileResult.value.sourceHost
                : '';
            const paymentProfileMerchantAuth0Ids = Array.isArray(storePaymentProfilePayload && storePaymentProfilePayload.merchant_auth0_ids)
              ? [...new Set(storePaymentProfilePayload.merchant_auth0_ids.filter(Boolean).map(candidateAuth0Id => String(candidateAuth0Id)))]
              : [];
            const paymentProfileMerchantUsers = Array.isArray(storePaymentProfilePayload && storePaymentProfilePayload.merchant_users)
              ? storePaymentProfilePayload.merchant_users
              : [];
            const adminUsersDataset =
              adminUsersByStoreResult && adminUsersByStoreResult.status === 'fulfilled' && adminUsersByStoreResult.value && Array.isArray(adminUsersByStoreResult.value.users)
                ? adminUsersByStoreResult.value.users
                : [];
            const adminUsersSourceHost =
              adminUsersByStoreResult && adminUsersByStoreResult.status === 'fulfilled' && adminUsersByStoreResult.value
                ? adminUsersByStoreResult.value.sourceHost
                : '';
            const ownershipMerchantAuth0Ids = (coreResult.ownershipFallbackUsers || [])
              .map(candidateUser => candidateUser && candidateUser.auth0_id ? String(candidateUser.auth0_id) : '')
              .filter(Boolean);
            const currentAuth0Id = getCurrentAuth0Id();
            const currentMerchantStoreAccessPayload = currentAuth0Id
              ? await fetchMerchantStoreAccessPayloadAcrossHosts(currentAuth0Id)
              : null;

            const currentMerchantLinkedUsers = resolveStoreAccessLinkedUsersForStore({
              payload: currentMerchantStoreAccessPayload,
              storeId
            });

            const merchantUsersByAuth0Id = new Map(
              usersByStore
                .filter(candidateUser => candidateUser && candidateUser.auth0_id)
                .map(candidateUser => [String(candidateUser.auth0_id), candidateUser])
            );
            const paymentProfileFallbackUsers = paymentProfileMerchantAuth0Ids.map(candidateAuth0Id => {
              if (merchantUsersByAuth0Id.has(candidateAuth0Id)) {
                return merchantUsersByAuth0Id.get(candidateAuth0Id);
              }

              return {
                auth0_id: candidateAuth0Id,
                username: candidateAuth0Id,
                firstname: '',
                lastname: '',
                email: '',
                phone: '',
                Roles: [{ role_id: '', role_description: 'store_user', role: 220 }]
              };
            });
            const paymentProfilePayloadUsers = paymentProfileMerchantUsers
              .filter(candidateUser => candidateUser && candidateUser.auth0_id)
              .map(candidateUser => ({
                auth0_id: candidateUser.auth0_id || '',
                user_id: candidateUser.user_id || '',
                username: candidateUser.email || candidateUser.auth0_id || '',
                firstname: candidateUser.firstname || '',
                lastname: candidateUser.lastname || '',
                email: candidateUser.email || '',
                phone: candidateUser.phone || '',
                access_users: Array.isArray(candidateUser.access_users) ? candidateUser.access_users : [],
                Roles: Array.isArray(candidateUser.roles)
                  ? candidateUser.roles.map(roleRecord => ({
                      role_id: '',
                      role_description: roleRecord && roleRecord.role_description ? roleRecord.role_description : '',
                      role: roleRecord && Number.isInteger(roleRecord.role) ? roleRecord.role : 220,
                    }))
                  : [{ role_id: '', role_description: 'store_user', role: 220 }]
              }));

            const adminLinkedUsers = resolveAssociatedUsersFromAdminDataset({
              adminUsers: adminUsersDataset,
              storeId,
              ownershipMerchantAuth0Ids,
              paymentProfileMerchantAuth0Ids,
              hasuraUsers: usersByStore,
            });

            const effectiveUsers = dedupeAssociatedUsers([
              ...usersByStore,
              ...coreResult.ownershipFallbackUsers,
              ...paymentProfileFallbackUsers,
              ...paymentProfilePayloadUsers,
              ...currentMerchantLinkedUsers,
              ...adminLinkedUsers,
            ]);

            const fallbackAccountingEmail = getFallbackAccountingEmail(coreResult.store) || getFallbackAccountingEmailFromUsers(effectiveUsers);
            const nextPaymentProfile = buildPaymentProfileState({
              billingEmail: fallbackAccountingEmail,
              invoiceFrequency: getStoredStoreInvoiceFrequency(storeId)
            });

            setAssociatedUsers(previousUsers => dedupeAssociatedUsers([...(Array.isArray(previousUsers) ? previousUsers : []), ...effectiveUsers]));
            setAssociatedUsersDiagnostics({
              usersHasuraCount: usersByStore.length,
              usersHasuraExpandedCount: 0,
              ownershipFallbackCount: coreResult.ownershipFallbackUsers.length,
              paymentProfileMerchantCount: paymentProfileMerchantAuth0Ids.length + paymentProfilePayloadUsers.length,
              adminUsersStoreCount: adminLinkedUsers.length + currentMerchantLinkedUsers.length,
              storeAccessCandidatesCount: currentAuth0Id ? 1 : 0,
              finalCount: effectiveUsers.length,
              adminUsersSourceHost,
              storePaymentProfileSourceHost,
            });
            if (!isBillingStale()) {
              setPaymentProfile(nextPaymentProfile);
              setSavedPaymentProfile(nextPaymentProfile);
            }
          })
          .catch(async () => {
            if (!isActiveRequest()) {
              return;
            }

            const ownershipAuth0Candidates = (coreResult.ownershipFallbackUsers || [])
              .map(candidateUser => (candidateUser && candidateUser.auth0_id ? String(candidateUser.auth0_id) : ''))
              .filter(Boolean);

            let fallbackStoreAccessUsers = [];

            try {
              const ownershipStoreAccessResponses = await Promise.allSettled(
                ownershipAuth0Candidates.map(candidateAuth0Id =>
                  fetchMerchantStoreAccessPayloadAcrossHosts(candidateAuth0Id)
                )
              );

              const fallbackStoreAccessPayloads = ownershipStoreAccessResponses
                .filter(candidateResponse => candidateResponse.status === 'fulfilled' && candidateResponse.value)
                .map(candidateResponse => candidateResponse.value);

              fallbackStoreAccessUsers = fallbackStoreAccessPayloads
                .flatMap(candidatePayload => Array.isArray(candidatePayload.linked_merchants) ? candidatePayload.linked_merchants : [])
                .filter(Boolean)
                .map(candidateUser => ({
                  auth0_id: candidateUser.auth0_id || '',
                  user_id: candidateUser.user_id || '',
                  username: candidateUser.username || candidateUser.email || '',
                  firstname: candidateUser.firstname || '',
                  lastname: candidateUser.lastname || '',
                  email: candidateUser.email || '',
                  phone: candidateUser.phone || '',
                  access_users: Array.isArray(candidateUser.access_users) ? candidateUser.access_users : [],
                  Roles: Array.isArray(candidateUser.roles)
                    ? candidateUser.roles.map(roleRecord => ({
                        role_id: '',
                        role_description: roleRecord && roleRecord.role_description ? roleRecord.role_description : '',
                        role: roleRecord && Number.isInteger(roleRecord.role) ? roleRecord.role : 220,
                      }))
                    : [{ role_id: '', role_description: 'store_user', role: 220 }]
                }));
            } catch (error) {
              fallbackStoreAccessUsers = [];
            }

            const fallbackUsers = sortUsersByName(
              [...(coreResult.ownershipFallbackUsers || []), ...fallbackStoreAccessUsers]
                .filter(Boolean)
                .reduce((dedupedUsers, candidateUser) => {
                  const dedupeKey =
                    (candidateUser && candidateUser.auth0_id && String(candidateUser.auth0_id))
                    || (candidateUser && candidateUser.email && String(candidateUser.email).toLowerCase())
                    || null;

                  if (!dedupeKey) {
                    dedupedUsers.push(candidateUser);
                    return dedupedUsers;
                  }

                  if (!dedupedUsers.some(existingUser => {
                    const existingKey =
                      (existingUser && existingUser.auth0_id && String(existingUser.auth0_id))
                      || (existingUser && existingUser.email && String(existingUser.email).toLowerCase())
                      || null;

                    return existingKey === dedupeKey;
                  })) {
                    dedupedUsers.push(candidateUser);
                  }

                  return dedupedUsers;
                }, [])
            );

            if (fallbackUsers.length > 0) {
              setAssociatedUsers(fallbackUsers);
            }

            setAssociatedUsersDiagnostics({
              usersHasuraCount: 0,
              usersHasuraExpandedCount: 0,
              ownershipFallbackCount: (coreResult.ownershipFallbackUsers || []).length,
              paymentProfileMerchantCount: 0,
              adminUsersStoreCount: fallbackStoreAccessUsers.length,
              storeAccessCandidatesCount: ownershipAuth0Candidates.length,
              finalCount: fallbackUsers.length,
              adminUsersSourceHost: '',
              storePaymentProfileSourceHost: 'store-access-fallback',
            });
          });
      })
      .catch(error => {
        if (!isActiveRequest()) {
          return;
        }

        setSelectedStoreDetail(null);
        setAssociatedUsers([]);
        setOriginalHours(createEmptyHours());
        setDraftHours(createEmptyHours());
        if (!isBillingStale()) {
          setPaymentProfile(buildPaymentProfileState());
          setSavedPaymentProfile(buildPaymentProfileState());
        }
        setDetailError(getRequestErrorMessage(error, 'Unable to load store details.'));
      })
      .finally(() => {
        if (!isActiveRequest()) {
          return;
        }

        window.clearTimeout(loadingGuardId);
        setDetailLoading(false);
      });
  }

  const updateDraftHours = (dayIndex, field, value) => {
    setDraftHours(previousHours =>
      previousHours.map(day => {
        if (day.day_of_week !== dayIndex) {
          return day;
        }

        if (field === 'is_closed') {
          return {
            ...day,
            is_closed: value,
            open_time: value ? '' : day.open_time,
            close_time: value ? '' : day.close_time
          };
        }

        return {
          ...day,
          is_closed: false,
          [field]: value
        };
      })
    );
  };

  const updateStoreForm = (field, value) => {
    setStoreForm(previousForm => ({
      ...previousForm,
      [field]: value
    }));
  };

  const startEditingUser = user => {
    setEditingUserId(user.auth0_id || '');
    setUserForm(buildUserForm(user));
    setUserNotice('');
  };

  const cancelEditingUser = () => {
    setEditingUserId('');
    setUserForm(buildUserForm(null));
  };

  const handleAddUserResult = message => {
    setUserNotice(message);

    if (message === 'Successfully added Account') {
      setShowAddUserModal(false);
      if (selectedStoreId) {
        getStoreDetails(selectedStoreId);
      }
    }
  };

  const handleCreateMerchantResult = message => {
    setStoreNotice(message);

    if (message === 'Successfully added merchant') {
      setShowCreateMerchantModal(false);
      getStores(role);
    }
  };

  const saveStoreDetails = async () => {
    if (!selectedStoreId) {
      return;
    }

    setStoreSaving(true);
    setStoreNotice('');
    setPaymentProfileError('');

    const primaryOwner = getPrimaryStoreOwner(selectedStoreDetail);
    const merchantAuth0Id = resolveMerchantAuth0Id({ primaryOwner, associatedUsers, isAdminView: true });
    const canSaveBilling = Boolean(selectedStoreId);
    const effectiveBillingEmail =
      paymentProfile.billing_email ||
      savedPaymentProfile.billing_email ||
      getFallbackAccountingEmail(selectedStoreDetail) ||
      getFallbackAccountingEmailFromUsers(associatedUsers) ||
      '';
    const shouldSaveStore = hasStoreFormChanges(selectedStoreDetail, storeForm);
    const shouldSaveBilling = canSaveBilling && (
      (effectiveBillingEmail || '') !== (savedPaymentProfile.billing_email || '') ||
      (isAdminView && getPaymentTermSelectValue(paymentProfile.invoice_frequency) !== getPaymentTermSelectValue(savedPaymentProfile.invoice_frequency))
    );
    const shouldSyncDirectType =
      getDeliveryTypeValue(selectedStoreDetail && selectedStoreDetail.direct_type) !== getDeliveryTypeValue(storeForm.direct_type);
    let storeUpdated = false;
    let billingUpdated = false;
    let storeUpdatePayload = null;

    if (!shouldSaveStore && !shouldSaveBilling) {
      isStoreEditModeRef.current = false;
      setStoreEditMode(false);
      setStoreNotice('No changes to save.');
      setStoreSaving(false);
      return;
    }

    try {
      if (shouldSaveStore) {
        const nextDirectType = normalizeStoreDirectType(storeForm.direct_type);
        storeUpdatePayload = buildStoreUpdatePayload({
          store: selectedStoreDetail,
          storeForm,
          updatedBy: getCurrentAuth0Id()
        });

        await axios({
          method: 'PUT',
          url: inventory_urls.editStore + '/' + selectedStoreId,
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('access_token')
          },
          data: storeUpdatePayload
        });

        if (shouldSyncDirectType) {
          await persistStoreDirectType({
            storeId: selectedStoreId,
            directType: nextDirectType
          });
        }

        storeUpdated = true;
        setStoredStoreDeliveryTypeOverride(selectedStoreId, nextDirectType);

        setSelectedStoreDetail(previousStore => ({
          ...previousStore,
          name: storeForm.name,
          phone: storeForm.phone,
          address_1: storeForm.address_1,
          address_2: storeForm.address_2,
          city: storeForm.city,
          state: storeForm.state,
          zip: storeForm.zip,
          timezone: storeForm.timezone,
          direct_type: nextDirectType
        }));
        setStores(previousStores =>
          previousStores.map(store =>
            store.store_id === selectedStoreId
              ? {
                  ...store,
                  name: storeForm.name,
                  phone: storeForm.phone,
                  address_1: storeForm.address_1,
                  address_2: storeForm.address_2,
                  city: storeForm.city,
                  state: storeForm.state,
                  direct_type: nextDirectType
                }
              : store
          )
        );
      }

      if (shouldSaveBilling) {
        const updatedPaymentProfile = await saveMerchantPaymentProfile({
          primaryOwner,
          merchantAuth0Id,
          associatedUsers,
          billingEmail: effectiveBillingEmail,
          invoiceFrequency: paymentProfile.invoice_frequency,
          storeId: selectedStoreId,
          storeName: storeForm.name,
          isAdminView
        });

        if (updatedPaymentProfile) {
          const nextPaymentProfile = buildPaymentProfileState({
            billingEmail: updatedPaymentProfile.billing_email || paymentProfile.billing_email,
            invoiceFrequency: updatedPaymentProfile.invoice_frequency,
            fallbackInvoiceFrequency: paymentProfile.invoice_frequency
          });

          if (selectedStoreId && nextPaymentProfile.invoice_frequency) {
            setStoredStoreInvoiceFrequency(selectedStoreId, nextPaymentProfile.invoice_frequency);
          }

          setStoredStorePaymentProfile(selectedStoreId, nextPaymentProfile);

          billingSavedAtRef.current = Date.now();

          setPaymentProfile(nextPaymentProfile);
          setSavedPaymentProfile(nextPaymentProfile);
        }

        billingUpdated = true;
      }

      isStoreEditModeRef.current = false;
      setStoreEditMode(false);

      if (storeUpdated && billingUpdated) {
        setStoreNotice('Store details updated.');
      } else if (billingUpdated) {
        setStoreNotice('Billing settings updated.');
      } else {
        setStoreNotice('Store details updated.');
      }
    } catch (error) {
      if (storeUpdated) {
        setStoreNotice('Store details updated, but billing settings could not be updated.');
      } else if (billingUpdated) {
        setStoreNotice('Billing settings updated, but store details could not be updated.');
      } else {
        const failedRequestUrl = shouldSaveStore && storeUpdatePayload
          ? inventory_urls.editStore + '/' + selectedStoreId
          : getAdminStorePaymentProfileUrls(selectedStoreId)[0] || inventory_urls.editStore + '/' + selectedStoreId;

        console.error('Store update failed', {
          storeId: selectedStoreId,
          requestUrl: failedRequestUrl,
          requestData: storeUpdatePayload,
          responseStatus: error && error.response ? error.response.status : null,
          responseData: error && error.response ? error.response.data : null,
          errorMessage: error && error.message ? error.message : null,
        });
        setStoreNotice(getRequestErrorMessage(error, 'Unable to update store details.'));
      }
    } finally {
      setStoreSaving(false);
    }
  };

  const saveUserDetails = user => {
    if (!user || !user.auth0_id) {
      return;
    }

    setUserSavingId(user.auth0_id);
    setUserNotice('');

    const currentRole = getUserPrimaryRole(user);
    const hasProfileChanges =
      (user.firstname || '') !== userForm.firstname ||
      (user.lastname || '') !== userForm.lastname ||
      (user.email || '') !== userForm.email ||
      (user.phone || '') !== userForm.phone_number;
    const hasRoleChanges = currentRole && String(currentRole.role) !== userForm.role;

    if (!hasProfileChanges && !hasRoleChanges) {
      setUserSavingId('');
      setEditingUserId('');
      setUserForm(buildUserForm(null));
      setUserNotice('No changes to save.');
      return;
    }

    const requests = [];
    const currentUserAuth0Id = getCurrentAuth0Id();

    if (hasProfileChanges) {
      const changedProfileFields = buildChangedUserEditPayload({
        oldFirstName: user.firstname || '',
        firstname: userForm.firstname,
        oldLastName: user.lastname || '',
        lastname: userForm.lastname,
        oldEmail: user.email || '',
        email: userForm.email,
        oldPhoneNumber: user.phone || '',
        phone_number: userForm.phone_number
      });

      requests.push(
        axios({
          method: 'PUT',
          url: isAdminView ? getAdminEditUserUrl(user.auth0_id) : getMerchantEditUserUrl(user.auth0_id),
          withCredentials: true,
          timeout: USER_EDIT_REQUEST_TIMEOUT_MS,
          headers: {
            'content-type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('access_token')
          },
          data: changedProfileFields
        }).then(response => {
          const validationMessage = validateUserEditResponse(response, changedProfileFields);

          if (validationMessage) {
            throw new Error(validationMessage);
          }

          return getUserEditResponsePayload(response) || null;
        })
      );
    }

    if (hasRoleChanges) {
      const nextRoleValue = Number(userForm.role);
      const currentRoleId = currentRole && currentRole.role_id ? currentRole.role_id : '';
      const nextRoleDescription = ASSOCIATED_USER_ROLE_LABELS[userForm.role] || currentRole.role_description || '';

      requests.push(
        axios({
          method: 'PUT',
          url: merchant_urls.editMerchantRole + user.auth0_id + '/role',
          withCredentials: true,
          headers: {
            'content-type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('access_token')
          },
          data: {
            role: nextRoleValue
          }
        }).catch(async error => {
          const statusCode = error && error.response ? error.response.status : null;

          if (statusCode !== 404) {
            throw error;
          }

          return updateAssociatedUserRole({
            roleId: currentRoleId,
            nextRoleValue,
            roleDescription: nextRoleDescription,
            updatedBy: currentUserAuth0Id
          });
        })
      );
    }

    Promise.all(requests)
      .then(([editedUser]) => {
        const nextFirstName = editedUser && editedUser.firstname !== undefined ? editedUser.firstname : userForm.firstname;
        const nextLastName = editedUser && editedUser.lastname !== undefined ? editedUser.lastname : userForm.lastname;
        const nextEmail = editedUser && editedUser.email !== undefined ? editedUser.email : userForm.email;
        const nextPhone = editedUser && editedUser.phone !== undefined
          ? editedUser.phone
          : editedUser && editedUser.phone_number !== undefined
            ? editedUser.phone_number
            : userForm.phone_number;

        setAssociatedUsers(previousUsers =>
          sortUsersByName(
            previousUsers.map(existingUser =>
              existingUser.auth0_id === user.auth0_id
                ? {
                    ...existingUser,
                    firstname: nextFirstName,
                    lastname: nextLastName,
                    email: nextEmail,
                    phone: nextPhone,
                    Roles: existingUser.Roles && existingUser.Roles[0]
                      ? [
                          {
                            ...existingUser.Roles[0],
                            role: userForm.role ? Number(userForm.role) : existingUser.Roles[0].role,
                            role_description:
                              (ASSOCIATED_USER_ROLE_OPTIONS.find(option => option.value === userForm.role) || {}).label || existingUser.Roles[0].role_description
                          }
                        ]
                      : existingUser.Roles
                  }
                : existingUser
            )
          )
        );
        setEditingUserId('');
        setUserForm(buildUserForm(null));
        setUserNotice('User details updated.');
      })
      .catch(error => {
        setUserNotice(getRequestErrorMessage(error, 'Unable to update user details.'));
      })
      .finally(() => {
        setUserSavingId('');
      });
  };

  const saveHours = () => {
    if (!selectedStoreId) {
      return;
    }

    const toCreate = { days: [] };
    const toEdit = { days: [] };
    const toDelete = { days: [] };

    draftHours.forEach(day => {
      const originalDay = originalHours.find(existingDay => existingDay.day_of_week === day.day_of_week) || {
        open_time: '',
        close_time: '',
        is_closed: true
      };
      const originalHasHours = !originalDay.is_closed && !!originalDay.open_time && !!originalDay.close_time;
      const nextHasHours = !day.is_closed && !!day.open_time && !!day.close_time;

      if (nextHasHours && !originalHasHours) {
        toCreate.days.push({
          day_of_week: day.day_of_week,
          open_time: day.open_time,
          close_time: day.close_time,
          is_closed: false
        });
        return;
      }

      if (nextHasHours && originalHasHours && (day.open_time !== originalDay.open_time || day.close_time !== originalDay.close_time)) {
        toEdit.days.push({
          day_of_week: day.day_of_week,
          open_time: day.open_time,
          close_time: day.close_time,
          is_closed: false
        });
        return;
      }

      if (!nextHasHours && originalHasHours) {
        toDelete.days.push({
          day_of_week: day.day_of_week,
          open_time: null,
          close_time: null,
          is_closed: true
        });
      }
    });

    if (toCreate.days.length === 0 && toEdit.days.length === 0 && toDelete.days.length === 0) {
      setHoursNotice('No hours changes to save.');
      return;
    }

    setHoursSaving(true);
    setHoursNotice('');

    const headers = {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + localStorage.getItem('access_token')
    };
    const requests = [];

    if (toCreate.days.length > 0) {
      requests.push(
        axios({
          method: 'POST',
          url: inventory_urls.createRegularHours + selectedStoreId + '/hours/create',
          headers,
          data: toCreate
        })
      );
    }

    if (toEdit.days.length > 0) {
      requests.push(
        axios({
          method: 'PUT',
          url: inventory_urls.editRegularHours + selectedStoreId + '/hours/edit',
          headers,
          data: toEdit
        })
      );
    }

    if (toDelete.days.length > 0) {
      requests.push(
        axios({
          method: 'PUT',
          url: inventory_urls.deleteRegularHours + selectedStoreId + '/hours/edit',
          headers,
          data: toDelete
        })
      );
    }

    Promise.all(requests)
      .then(() => {
        setHoursNotice('Hours updated.');
        getStoreDetails(selectedStoreId);
      })
      .catch(error => {
        setHoursNotice(getRequestErrorMessage(error, 'Unable to save store hours.'));
      })
      .finally(() => {
        setHoursSaving(false);
      });
  };

  const getUserRole = async () => {
    let roles = await getRoles();
    if (roles !== 'error') {
      setRole(roles[0]);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserRole();
  }, []);

  useEffect(() => {
    if (role !== null) {
      getStores(role);
    }
  }, [currentUserStoreId, role, marketIdMerchants]);

  useEffect(() => {
    if (role === null) {
      return;
    }

    if (role < 200) {
      setCurrentUserStoreId('');
      setCurrentUserStoreLoading(false);
      return;
    }

    const merchantIdentity = getCurrentMerchantIdentity();
    const auth0Id = merchantIdentity.auth0Id;

    if (!auth0Id) {
      setCurrentUserStoreId('');
      setCurrentUserStoreLoading(false);
      return;
    }

    let active = true;
    const cachedStoreId = getStoredCurrentUserStoreId(auth0Id);

    if (cachedStoreId) {
      setCurrentUserStoreId(cachedStoreId);
      setCurrentUserStoreLoading(false);
    } else {
      setCurrentUserStoreLoading(true);
    }

    const applyResolvedCurrentUserStoreId = nextStoreId => {
      const normalizedStoreId = nextStoreId ? String(nextStoreId) : '';
      setCurrentUserStoreId(normalizedStoreId);
      setStoredCurrentUserStoreId(auth0Id, normalizedStoreId);
    };

    const fallbackIdentityClauses = [
      merchantIdentity.email ? `{email: {_ilike: "${escapeGraphqlString(merchantIdentity.email)}"}}` : '',
      merchantIdentity.username ? `{username: {_ilike: "${escapeGraphqlString(merchantIdentity.username)}"}}` : ''
    ].filter(Boolean);

    const fallbackIdentityQuery = fallbackIdentityClauses.length > 0
      ? `query FallbackCurrentMerchantStore { Users(where: {_or: [${fallbackIdentityClauses.join(', ')}]}, limit: 1) { store_id } }`
      : '';

    axios({
      method: 'post',
      url: user_urls.hasuraHost,
      headers: {
        Authorization: 'Bearer ' + getPreferredAuthToken(),
        'content-type': 'application/json'
      },
      data: {
        query: `{Users(where: {auth0_id: {_eq: "${auth0Id}"}}) {store_id}}`
      }
    })
      .then(async response => {
        if (!active) {
          return;
        }

        const merchantStoreIdFromAccess = await fetchMerchantStoreIdFromAccessEndpoint(auth0Id);

        if (!active) {
          return;
        }

        if (merchantStoreIdFromAccess) {
          applyResolvedCurrentUserStoreId(merchantStoreIdFromAccess);
          return;
        }

        const userRecord = response && response.data && response.data.data && response.data.data.Users ? response.data.data.Users[0] : null;

        if ((!userRecord || !userRecord.store_id) && fallbackIdentityQuery) {
          const fallbackResponse = await axios({
            method: 'post',
            url: user_urls.hasuraHost,
            headers: {
              Authorization: 'Bearer ' + getPreferredAuthToken(),
              'content-type': 'application/json'
            },
            data: {
              query: fallbackIdentityQuery
            }
          });

          const fallbackUserRecord = fallbackResponse && fallbackResponse.data && fallbackResponse.data.data && fallbackResponse.data.data.Users
            ? fallbackResponse.data.data.Users[0]
            : null;

          applyResolvedCurrentUserStoreId(fallbackUserRecord && fallbackUserRecord.store_id ? fallbackUserRecord.store_id : '');
          return;
        }

        if (userRecord && userRecord.store_id) {
          applyResolvedCurrentUserStoreId(userRecord.store_id);
          return;
        }

        const storeOwnershipResponse = await postInventoryHasuraQuery(
          `{Stores(where: {StoreOwnerships: {merchant_id: {_eq: "${auth0Id}"}}}, limit: 1) {store_id}}`
        );

        if (!active) {
          return;
        }

        const fallbackStore =
          storeOwnershipResponse && storeOwnershipResponse.data && storeOwnershipResponse.data.data && Array.isArray(storeOwnershipResponse.data.data.Stores)
            ? storeOwnershipResponse.data.data.Stores[0]
            : null;

        applyResolvedCurrentUserStoreId(fallbackStore && fallbackStore.store_id ? fallbackStore.store_id : '');
      })
      .catch(async () => {
        if (!active) {
          return;
        }

        const merchantStoreIdFromAccess = await fetchMerchantStoreIdFromAccessEndpoint(auth0Id);

        if (!active) {
          return;
        }

        applyResolvedCurrentUserStoreId(merchantStoreIdFromAccess || '');
      })
      .finally(() => {
        if (active) {
          setCurrentUserStoreLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [role]);

  const filteredStores = useMemo(
    () => stores
      .filter(store => {
        if (!isAdminView) {
          return true;
        }

        if (customerType === 'businesses') {
          return !store.is_individual_user;
        }

        return store.is_individual_user;
      })
      .filter(store => {
        const ownershipSearchText = (store.StoreOwnerships || [])
          .map(owner => [owner.firstname, owner.lastname, owner.email, owner.merchant_id].filter(Boolean).join(' '))
          .join(' ')
          .toLowerCase();
        const searchableText = [store.name, store.phone, store.address_1, store.address_2, store.city, store.state, ownershipSearchText]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (searchTerm.trim() !== '' && !searchableText.includes(searchTerm.trim().toLowerCase())) {
          return false;
        }

        if (cityFilter.trim() !== '' && !(store.city || '').toLowerCase().includes(cityFilter.trim().toLowerCase())) {
          return false;
        }

        if (stateFilter.trim() !== '' && !(store.state || '').toLowerCase().includes(stateFilter.trim().toLowerCase())) {
          return false;
        }

        return true;
      }),
    [stores, customerType, searchTerm, cityFilter, stateFilter]
  );

  useEffect(() => {
    if (filteredStores.length === 0) {
      setSelectedStoreId(null);
      setSelectedStoreDetail(null);
      setAssociatedUsers([]);
      setOriginalHours(createEmptyHours());
      setDraftHours(createEmptyHours());
      return;
    }

    const hasSelectedStore = filteredStores.some(store => store.store_id === selectedStoreId);

    if (!hasSelectedStore) {
      setSelectedStoreId(filteredStores[0].store_id);
    }
  }, [filteredStores, selectedStoreId]);

  useEffect(() => {
    if (selectedStoreId) {
      setStoreEditMode(false);
      setUsersEditMode(false);
      setEditingUserId('');
      setUserForm(buildUserForm(null));
      setShowAddUserModal(false);
      setHoursExpanded(false);
      getStoreDetails(selectedStoreId);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    let isCurrent = true;
    const effectStartedAt = Date.now();

    if (isStoreEditModeRef.current) {
      setPaymentProfileLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    if (!selectedStoreDetail) {
      setPaymentProfile({ billing_email: '', invoice_frequency: 'single' });
      setSavedPaymentProfile({ billing_email: '', invoice_frequency: 'single' });
      setPaymentProfileLoading(false);
      setPaymentProfileError('');
      return () => {
        isCurrent = false;
      };
    }

    const primaryOwner = getPrimaryStoreOwner(selectedStoreDetail);
    const merchantAuth0Ids = isAdminView
      ? getMerchantAuth0Ids({ primaryOwner, associatedUsers, isAdminView })
      : [getCurrentAuth0Id()].filter(Boolean);
    const merchantAuth0Id = merchantAuth0Ids[0] || '';
    const fallbackAccountingEmail = isAdminView
      ? (getFallbackAccountingEmail(selectedStoreDetail) || getFallbackAccountingEmailFromUsers(associatedUsers))
      : getFallbackAccountingEmail(selectedStoreDetail);
    const cachedStoreInvoiceFrequency = getStoredStoreInvoiceFrequency(selectedStoreId);
    const cachedStorePaymentProfile = getStoredStorePaymentProfile(selectedStoreId);
    const fallbackPaymentProfile = buildPaymentProfileState({
      billingEmail: (cachedStorePaymentProfile && cachedStorePaymentProfile.billing_email) || fallbackAccountingEmail,
      invoiceFrequency: cachedStoreInvoiceFrequency || (cachedStorePaymentProfile && cachedStorePaymentProfile.invoice_frequency)
    });

    setPaymentProfile(fallbackPaymentProfile);
    setPaymentProfileError('');
    setSavedPaymentProfile(fallbackPaymentProfile);

    if (!selectedStoreId) {
      setPaymentProfileLoading(false);

      return () => {
        isCurrent = false;
      };
    }

    setPaymentProfileLoading(true);

    const paymentProfileLoadGuardId = window.setTimeout(() => {
      if (!isCurrent) {
        return;
      }

      setPaymentProfileLoading(false);
    }, STORE_PAYMENT_PROFILE_FAST_TIMEOUT_MS);

    fetchMerchantPaymentProfile({ merchantAuth0Id, merchantAuth0Ids, isAdminView, storeId: selectedStoreId })
      .then(nextPaymentProfile => {
        console.log('[BillingLoad] fetchMerchantPaymentProfile result', { storeId: selectedStoreId, merchantAuth0Id, invoice_frequency: nextPaymentProfile && nextPaymentProfile.invoice_frequency, billingSavedAtRef: billingSavedAtRef.current, effectStartedAt, isStale: billingSavedAtRef.current > effectStartedAt });
        if (!isCurrent || isStoreEditModeRef.current) {
          return;
        }

        // If a billing save completed after this effect started, the API response
        // may be stale (write hasn't propagated yet). Skip overwriting in that case.
        if (billingSavedAtRef.current > effectStartedAt) {
          return;
        }

        if (selectedStoreId && nextPaymentProfile && nextPaymentProfile.invoice_frequency) {
          setStoredStoreInvoiceFrequency(selectedStoreId, nextPaymentProfile.invoice_frequency);
        }

        const resolvedPaymentProfile = buildPaymentProfileState({
          billingEmail: (nextPaymentProfile && nextPaymentProfile.billing_email) || fallbackAccountingEmail,
          invoiceFrequency: nextPaymentProfile && nextPaymentProfile.invoice_frequency,
          fallbackInvoiceFrequency: cachedStoreInvoiceFrequency
        });

        setStoredStorePaymentProfile(selectedStoreId, resolvedPaymentProfile);
        setPaymentProfile(resolvedPaymentProfile);
        setSavedPaymentProfile(resolvedPaymentProfile);
      })
      .catch(() => {
        if (!isCurrent || isStoreEditModeRef.current) {
          return;
        }

        setPaymentProfile(fallbackPaymentProfile);
        setSavedPaymentProfile(fallbackPaymentProfile);
        setPaymentProfileError(cachedStoreInvoiceFrequency ? 'Unable to refresh payment terms. Showing last known terms.' : 'Unable to load payment terms.');
      })
      .finally(() => {
        if (isCurrent) {
          window.clearTimeout(paymentProfileLoadGuardId);
          setPaymentProfileLoading(false);
        }
      });

    return () => {
      isCurrent = false;
      window.clearTimeout(paymentProfileLoadGuardId);
    };
  }, [selectedStoreId, selectedStoreDetail, isAdminView]);

  const configuredHoursCount = draftHours.filter(day => !day.is_closed && day.open_time && day.close_time).length;
  const canEditBillingSettings = isAdminView && Boolean(selectedStoreId) && !storeSaving;
  const canEditAccountingEmail = Boolean(selectedStoreId) && !storeSaving;
  const paymentTermSelectValue = getPaymentTermSelectValue(paymentProfile.invoice_frequency);
  const deliveryTypeValue = getDeliveryTypeValue(storeForm.direct_type);
  const accountingEmailDisplay = paymentProfile.billing_email || getFallbackAccountingEmail(selectedStoreDetail) || 'No accounting email';
  const primaryOwner = getPrimaryStoreOwner(selectedStoreDetail);
  const orderHistoryStoreId = (selectedStoreDetail && selectedStoreDetail.store_id) || selectedStoreId || '';
  const orderHistoryUserIdentifier = resolveStoreOrdersUserIdentifier({ primaryOwner, associatedUsers, isAdminView });
  const orderHistoryPath = isAdminView
    ? buildStoreOrdersUserInfoPath(
        orderHistoryUserIdentifier,
        orderHistoryStoreId
      )
    : buildMerchantStoreOrdersUserInfoPath(
        props.match.path,
        orderHistoryUserIdentifier,
        orderHistoryStoreId
      );

  if (loading || (!isAdminView && currentUserStoreLoading && !currentUserStoreId)) return <Loading />;
  return (
    <>
      <Switch>
        <Route
          path={`${props.match.path}/users/:userId/store/:storeId`}
          render={routeProps => <User {...routeProps} role={role} />}
        />
        <Route
          path={`${props.match.path}/users/:userId`}
          render={routeProps => <User {...routeProps} role={role} />}
        />
        <Route path={`${props.match.path}/store/:id`} component={LandingContainer} />
        <Route
          exact
          path={props.match.path}
          render={() => (
            <div className="merchant-page-container" style={{ margin: `80px 0 10px 115px` }}>
              <MerchantListHeader
                className="merchant-list-header"
                role={role}
                canCreateStore={Boolean(isAdminView)}
                onCreateStore={isAdminView ? () => {
                  setShowCreateMerchantModal(true);
                  setStoreNotice('');
                } : null}
              />
              <Styles className="merchant-listing-container">
                <div className="customer-manager-layout">
                  <div className="customer-list-panel">
                    {isAdminView ? (
                      <>
                        <div className="customer-toggle-row">
                          <button
                            className={customerType === 'businesses' ? 'toggle-button active' : 'toggle-button'}
                            onClick={() => setCustomerType('businesses')}
                          >
                            Businesses
                          </button>
                          <button
                            className={customerType === 'individuals' ? 'toggle-button active' : 'toggle-button'}
                            onClick={() => setCustomerType('individuals')}
                          >
                            Individuals
                          </button>
                        </div>

                        <div className="filters-panel">
                          <input
                            className="customer-search-input"
                            type="text"
                            value={searchTerm}
                            onChange={event => setSearchTerm(event.target.value)}
                            placeholder="Search name, phone, address, email, owner..."
                          />
                          <div className="location-filter-row">
                            <input
                              className="customer-search-input half"
                              type="text"
                              value={cityFilter}
                              onChange={event => setCityFilter(event.target.value)}
                              placeholder="City"
                            />
                            <input
                              className="customer-search-input half"
                              type="text"
                              value={stateFilter}
                              onChange={event => setStateFilter(event.target.value)}
                              placeholder="State"
                            />
                          </div>
                        </div>

                        <div className="customer-results-meta">
                          <span>{filteredStores.length} results</span>
                        </div>
                      </>
                    ) : null}

                    <div className="customer-list">
                      {filteredStores.map(store => {
                        const primaryOwner = store.StoreOwnerships && store.StoreOwnerships.length > 0 ? store.StoreOwnerships[0] : null;

                        return (
                          <button
                            type="button"
                            key={store.store_id}
                            className={selectedStoreId === store.store_id ? 'customer-list-item selected' : 'customer-list-item'}
                            onClick={() => setSelectedStoreId(store.store_id)}
                          >
                            <div className="customer-list-item__body compact">
                              <div className="customer-list-item__header">
                                <span className={store.is_enabled ? 'status-dot enabled' : 'status-dot disabled'} />
                                <h5>{store.name || 'Unnamed Customer'}</h5>
                              </div>
                              <p>{[store.city, store.state].filter(Boolean).join(', ') || 'No city/state'}</p>
                              <p>{safeFormatPhone(store.phone) || 'No phone'}</p>
                              {primaryOwner && <p>{[primaryOwner.firstname, primaryOwner.lastname].filter(Boolean).join(' ') || primaryOwner.email}</p>}
                            </div>
                          </button>
                        );
                      })}

                      {filteredStores.length === 0 && (
                        <div className="customer-empty-state">{isAdminView ? `No matching ${customerType} found.` : 'Your store could not be loaded.'}</div>
                      )}
                    </div>
                  </div>

                  <div className="customer-placeholder-panel">
                    {detailLoading ? (
                      <div className="customer-placeholder-panel__content">
                        <h4>Loading store details...</h4>
                      </div>
                    ) : selectedStoreDetail ? (
                      <div className="store-detail-panel">
                        <div className="store-detail-card hero">
                          <div className="store-detail-card__header">
                            <div>
                              <span className="detail-eyebrow">{selectedStoreDetail.is_individual_user ? 'Individual' : 'Business'}</span>
                              <h3>{selectedStoreDetail.name || 'Unnamed Customer'}</h3>
                            </div>
                            <div className="store-detail-card__controls">
                              <button
                                type="button"
                                className={storeEditMode ? 'icon-edit-button active' : 'icon-edit-button'}
                                onClick={() => {
                                  const nextStoreEditMode = !storeEditMode;
                                  isStoreEditModeRef.current = nextStoreEditMode;
                                  setStoreEditMode(nextStoreEditMode);
                                  setStoreNotice('');
                                  setStoreForm(buildStoreForm(selectedStoreDetail));
                                }}
                                aria-label="Edit store details"
                              >
                                <i className="tim-icons icon-pencil" />
                              </button>
                              <div className="detail-status-stack">
                                <span className={selectedStoreDetail.is_enabled ? 'detail-status enabled' : 'detail-status disabled'}>
                                  {selectedStoreDetail.is_enabled ? 'Enabled' : 'Disabled'}
                                </span>
                                {isAdminView && (
                                  <>
                                    <label className="detail-select-group">
                                      <span>Payment Terms</span>
                                      <select
                                        className="detail-select"
                                        value={paymentTermSelectValue}
                                        onChange={event => {
                                          const invoiceFrequency = event.target.value;

                                          setPaymentProfile(previousProfile => ({
                                            ...previousProfile,
                                            invoice_frequency: invoiceFrequency
                                          }));
                                          setPaymentProfileError('');
                                        }}
                                        disabled={!storeEditMode || !canEditBillingSettings}
                                      >
                                        {!isKnownPaymentTermValue(paymentProfile.invoice_frequency) && (
                                          <option value={paymentTermSelectValue}>{getPaymentTermLabel(paymentProfile.invoice_frequency)} (Current)</option>
                                        )}
                                        <option value="single">Prepay</option>
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="net_14">Net 14</option>
                                        <option value="net_30">Net 30</option>
                                        <option value="net_60">Net 60</option>
                                        <option value="net_120">Net 120</option>
                                      </select>
                                    </label>
                                    <label className="detail-select-group">
                                      <span>Delivery Type</span>
                                      <select
                                        className="detail-select"
                                        value={deliveryTypeValue}
                                        onChange={event => updateStoreForm('direct_type', normalizeStoreDirectType([event.target.value]))}
                                        disabled={!storeEditMode || storeSaving}
                                      >
                                        {DELIVERY_TYPE_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                  </>
                                )}
                                {!isAdminView && (
                                  <div className="detail-inline-notice">
                                    Contact Gophr Support to change payment terms or delivery type.
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {storeNotice && <div className="detail-inline-notice">{storeNotice}</div>}

                          <div className="store-detail-grid compact-info">
                            <div>
                              <label>Store ID</label>
                              <p>{selectedStoreDetail.store_id}</p>
                            </div>
                            <div>
                              <label>Store Name</label>
                              {storeEditMode ? (
                                <input className="detail-input" type="text" value={storeForm.name} onChange={event => updateStoreForm('name', event.target.value)} />
                              ) : (
                                <p>{selectedStoreDetail.name || 'Unnamed Customer'}</p>
                              )}
                            </div>
                            <div>
                              <label>Phone Number</label>
                              {storeEditMode ? (
                                <input className="detail-input" type="text" value={storeForm.phone} onChange={event => updateStoreForm('phone', event.target.value)} />
                              ) : (
                                <p>{safeFormatPhone(selectedStoreDetail.phone) || 'No phone'}</p>
                              )}
                            </div>
                            <div className="span-2">
                              <label>{storeEditMode ? 'Street Address' : 'Address'}</label>
                              {storeEditMode ? (
                                <input className="detail-input" type="text" value={storeForm.address_1} onChange={event => updateStoreForm('address_1', event.target.value)} />
                              ) : (
                                <p>
                                  {[selectedStoreDetail.address_1, selectedStoreDetail.address_2, selectedStoreDetail.city, selectedStoreDetail.state, selectedStoreDetail.zip]
                                    .filter(Boolean)
                                    .join(', ') || 'No address'}
                                </p>
                              )}
                            </div>
                            {storeEditMode && (
                              <>
                                <div className="span-2">
                                  <label>Address Line 2</label>
                                  <input className="detail-input" type="text" value={storeForm.address_2} onChange={event => updateStoreForm('address_2', event.target.value)} />
                                </div>
                                <div>
                                  <label>City</label>
                                  <input className="detail-input" type="text" value={storeForm.city} onChange={event => updateStoreForm('city', event.target.value)} />
                                </div>
                                <div>
                                  <label>State</label>
                                  <input className="detail-input" type="text" value={storeForm.state} onChange={event => updateStoreForm('state', event.target.value)} />
                                </div>
                                <div>
                                  <label>Zip</label>
                                  <input className="detail-input" type="text" value={storeForm.zip} onChange={event => updateStoreForm('zip', event.target.value)} />
                                </div>
                              </>
                            )}
                            <div>
                              <label>Accounting Email</label>
                              {storeEditMode ? (
                                <input
                                  className="detail-input"
                                  type="email"
                                  value={paymentProfile.billing_email}
                                  onChange={event => {
                                    const billingEmail = event.target.value;

                                    setPaymentProfile(previousProfile => ({
                                      ...previousProfile,
                                      billing_email: billingEmail
                                    }));
                                    setPaymentProfileError('');
                                  }}
                                  disabled={!canEditAccountingEmail}
                                  placeholder="Accounting email"
                                />
                              ) : (
                                <p>{accountingEmailDisplay}</p>
                              )}
                            </div>
                            <div>
                              <label>Timezone</label>
                              {storeEditMode ? (
                                <input className="detail-input" type="text" value={storeForm.timezone} onChange={event => updateStoreForm('timezone', event.target.value)} />
                              ) : (
                                <p>{selectedStoreDetail.timezone || 'Not set'}</p>
                              )}
                            </div>
                            <div>
                              <label>Delivery Type</label>
                              <p>{getDeliveryTypeLabel(selectedStoreDetail.direct_type)}</p>
                            </div>
                            {!isAdminView && (
                              <div>
                                <label>Payment Terms</label>
                                <p>{paymentProfileLoading ? 'Loading...' : getPaymentTermLabel(paymentProfile.invoice_frequency)}</p>
                              </div>
                            )}
                          </div>

                          <div className="detail-action-row">
                            <button
                              type="button"
                              className="save-hours-button"
                              onClick={() =>
                                props.history.push({
                                  pathname: orderHistoryPath,
                                  state: {
                                    storeId: orderHistoryStoreId,
                                    storeName: selectedStoreDetail && selectedStoreDetail.name ? selectedStoreDetail.name : ''
                                  }
                                })
                              }
                              disabled={!orderHistoryUserIdentifier}
                            >
                              View Orders
                            </button>
                          </div>

                          {paymentProfileError && <div className="detail-inline-notice">{paymentProfileError}</div>}

                          {storeEditMode && (
                            <div className="detail-action-row">
                              <button type="button" className="save-hours-button" onClick={saveStoreDetails} disabled={storeSaving}>
                                {storeSaving ? 'Saving...' : 'Save Store'}
                              </button>
                              <button
                                type="button"
                                className="secondary-detail-button"
                                onClick={() => {
                                  isStoreEditModeRef.current = false;
                                  setStoreEditMode(false);
                                  setStoreForm(buildStoreForm(selectedStoreDetail));
                                  setPaymentProfile(savedPaymentProfile);
                                  setStoreNotice('');
                                  setPaymentProfileError('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>

                        {isAdminView && selectedStoreId && (
                          <StoreEntitlementsPanel storeId={selectedStoreId} authToken={getPreferredAuthToken()} />
                        )}

                        <div className="store-detail-card">
                          <div className="store-detail-card__header section">
                            <button type="button" className="hours-section-toggle" onClick={() => setHoursExpanded(!hoursExpanded)}>
                              <h4>Operating Hours</h4>
                              <p>{hoursExpanded ? 'Edit store hours directly here.' : `${configuredHoursCount} configured day${configuredHoursCount === 1 ? '' : 's'} • Click to expand`}</p>
                            </button>
                            {hoursExpanded && (
                              <button type="button" className="save-hours-button" onClick={saveHours} disabled={hoursSaving}>
                                {hoursSaving ? 'Saving...' : 'Save Hours'}
                              </button>
                            )}
                          </div>

                          {hoursNotice && <div className="hours-notice">{hoursNotice}</div>}

                          {hoursExpanded ? (
                            <div className="hours-table">
                              <div className="hours-table__head">
                                <span>Day</span>
                                <span>Open</span>
                                <span>Close</span>
                                <span>Closed</span>
                              </div>
                              {draftHours.map(day => (
                                <div className="hours-table__row" key={day.day_of_week}>
                                  <span>{day.dayLabel}</span>
                                  <input
                                    type="time"
                                    value={day.open_time || ''}
                                    onChange={event => updateDraftHours(day.day_of_week, 'open_time', event.target.value)}
                                    disabled={day.is_closed}
                                  />
                                  <input
                                    type="time"
                                    value={day.close_time || ''}
                                    onChange={event => updateDraftHours(day.day_of_week, 'close_time', event.target.value)}
                                    disabled={day.is_closed}
                                  />
                                  <label className="hours-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={day.is_closed}
                                      onChange={event => updateDraftHours(day.day_of_week, 'is_closed', event.target.checked)}
                                    />
                                    <span>Closed</span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="hours-collapsed-summary">
                              {draftHours.map(day => (
                                <div className="hours-collapsed-summary__row" key={day.day_of_week}>
                                  <span>{day.dayLabel}</span>
                                  <strong>{day.is_closed || !day.open_time || !day.close_time ? 'Closed' : `${day.open_time} - ${day.close_time}`}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="store-detail-card users">
                          <div className="store-detail-card__header section">
                            <div>
                              <h4>Associated Users</h4>
                            </div>
                            <div className="store-detail-card__controls">
                              {selectedStoreId && assignableUserRoleOptions.length > 0 && (
                                <button
                                  type="button"
                                  className="table-edit-button primary add-user-button"
                                  onClick={() => {
                                    setShowAddUserModal(true);
                                    setUserNotice('');
                                  }}
                                >
                                  Add User
                                </button>
                              )}
                              <button
                                type="button"
                                className={usersEditMode ? 'icon-edit-button active' : 'icon-edit-button'}
                                onClick={() => {
                                  setUsersEditMode(!usersEditMode);
                                  setEditingUserId('');
                                  setUserForm(buildUserForm(null));
                                  setUserNotice('');
                                }}
                                aria-label="Edit associated users"
                              >
                                <i className="tim-icons icon-pencil" />
                              </button>
                              <span className="detail-count">{associatedUsers.length} users</span>
                            </div>
                          </div>

                          {userNotice && <div className="detail-inline-notice">{userNotice}</div>}

                          <div className="users-table-wrapper">
                            <table className="users-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Username</th>
                                  <th>Email</th>
                                  <th>Phone</th>
                                  <th>Role</th>
                                  <th>Auth0 ID</th>
                                  {usersEditMode && <th>Actions</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {associatedUsers.length > 0 ? (
                                  associatedUsers.map(user => (
                                    <tr key={user.auth0_id || user.username || user.email}>
                                      <td>
                                        {editingUserId === user.auth0_id ? (
                                          <div className="table-edit-stack">
                                            <input
                                              className="detail-input compact"
                                              type="text"
                                              value={userForm.firstname}
                                              onChange={event => {
                                                const nextValue = event.target.value;
                                                setUserForm(previousForm => ({ ...previousForm, firstname: nextValue }));
                                              }}
                                              placeholder="First name"
                                            />
                                            <input
                                              className="detail-input compact"
                                              type="text"
                                              value={userForm.lastname}
                                              onChange={event => {
                                                const nextValue = event.target.value;
                                                setUserForm(previousForm => ({ ...previousForm, lastname: nextValue }));
                                              }}
                                              placeholder="Last name"
                                            />
                                          </div>
                                        ) : (
                                          [user.firstname, user.lastname].filter(Boolean).join(' ') || 'Unknown'
                                        )}
                                      </td>
                                      <td>{user.username || 'N/A'}</td>
                                      <td>
                                        {editingUserId === user.auth0_id ? (
                                          <input
                                            className="detail-input compact"
                                            type="text"
                                            value={userForm.email}
                                            onChange={event => {
                                              const nextValue = event.target.value;
                                              setUserForm(previousForm => ({ ...previousForm, email: nextValue }));
                                            }}
                                            placeholder="Email"
                                          />
                                        ) : (
                                          user.email || 'N/A'
                                        )}
                                      </td>
                                      <td>
                                        {editingUserId === user.auth0_id ? (
                                          <input
                                            className="detail-input compact"
                                            type="text"
                                            value={userForm.phone_number}
                                            onChange={event => {
                                              const nextValue = event.target.value;
                                              setUserForm(previousForm => ({ ...previousForm, phone_number: nextValue }));
                                            }}
                                            placeholder="Phone"
                                          />
                                        ) : (
                                          safeFormatPhone(user.phone) || 'N/A'
                                        )}
                                      </td>
                                      <td>
                                        {editingUserId === user.auth0_id ? (
                                          <select
                                            className="detail-input compact"
                                            value={userForm.role}
                                            onChange={event => {
                                              const nextValue = event.target.value;
                                              setUserForm(previousForm => ({ ...previousForm, role: nextValue }));
                                            }}
                                          >
                                            <option value="">Select role</option>
                                            {getEditableUserRoleOptions(role, user).map(option => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        ) : (
                                          getUserRoleLabel(user)
                                        )}
                                      </td>
                                      <td className="users-table__auth0-id">
                                        {user.auth0_id || 'No auth0 id'}
                                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#8a94a6' }}>
                                          Last login:{' '}
                                          {(() => {
                                            const activity = associatedUserActivity[user.auth0_id];
                                            if (!activity) return '…';
                                            return activity.last_login ? new Date(activity.last_login).toLocaleString() : 'Never';
                                          })()}
                                        </div>
                                        <button
                                          type="button"
                                          className="table-edit-button"
                                          style={{ marginTop: '4px' }}
                                          onClick={() => sendPasswordResetForUser(user)}
                                          disabled={!(user && user.email)}
                                        >
                                          Reset Password
                                        </button>
                                        {passwordResetStatusById[(user.auth0_id || user.email)] ? (
                                          <div style={{ fontSize: '11px', marginTop: '2px' }}>{passwordResetStatusById[(user.auth0_id || user.email)]}</div>
                                        ) : null}
                                      </td>
                                      {usersEditMode && (
                                        <td>
                                          {editingUserId === user.auth0_id ? (
                                            <div className="table-action-row">
                                              <button
                                                type="button"
                                                className="table-edit-button primary"
                                                onClick={() => saveUserDetails(user)}
                                                disabled={userSavingId === user.auth0_id}
                                              >
                                                {userSavingId === user.auth0_id ? 'Saving...' : 'Save'}
                                              </button>
                                              <button type="button" className="table-edit-button" onClick={cancelEditingUser}>
                                                Cancel
                                              </button>
                                            </div>
                                          ) : (
                                            <button type="button" className="table-icon-button" onClick={() => startEditingUser(user)} aria-label="Edit user">
                                              <i className="tim-icons icon-pencil" />
                                            </button>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={usersEditMode ? '7' : '6'} className="users-empty-state">
                                      No users associated with this store.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="customer-placeholder-panel__content">
                        <h4>{isAdminView ? (customerType === 'businesses' ? 'Businesses' : 'Individuals') : 'My Store'}</h4>
                        <p>{isAdminView ? 'Select an entry from the list to open that customer profile.' : 'Store details were not found for your account.'}</p>
                        {detailError && <p className="detail-error-message">{detailError}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </Styles>
              <Modal isOpen={showAddUserModal} toggle={() => setShowAddUserModal(false)} size="lg">
                <ModalHeader toggle={() => setShowAddUserModal(false)}>Add Associated User</ModalHeader>
                <ModalBody className="add-user-modal__body">
                  {selectedStoreId ? (
                    <CreateSubMerchantWizard storeId={selectedStoreId} notify={handleAddUserResult} toggleDropdown={() => setShowAddUserModal(false)} />
                  ) : null}
                </ModalBody>
              </Modal>
              <Modal isOpen={showCreateMerchantModal} toggle={() => setShowCreateMerchantModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCreateMerchantModal(false)}>Add Customer</ModalHeader>
                <ModalBody className="add-user-modal__body">
                  <CreateMerchantWizard notify={handleCreateMerchantResult} toggleDropdown={() => setShowCreateMerchantModal(false)} />
                </ModalBody>
              </Modal>
            </div>
          )}
        />
      </Switch>
    </>
  );
}
